import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { toast } from "react-toastify";
import HlthtekFooterLogo from "../../assets/images/logo/NavLogo_new.png";
import {
  enrichLineItemsFromInvoiceTotals,
  getInvoicePaymentTotal,
  normalizeInvoiceLineItem,
} from "../appointments/appointmentInvoiceHelpers";

const INVOICE_PDF_FOOTER_TEXT =
  "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (visit: https://www.hlthtek.com)";

/** ISO 216 A5 portrait (mm). Passed explicitly so the PDF MediaBox is true A5, not A4. */
const INVOICE_PDF_A5_MM = [148, 210];

/**
 * Fallback page margins (mm) when get_invoice_config is unavailable.
 * Used for WITHOUT_LETTERHEAD and for LETTERHEAD + background template content inset.
 */
const INVOICE_PRINT_NO_LETTERHEAD_MARGIN_MM = {
  a4: { top: 30, right: 15, bottom: 20, left: 15 },
  a5: { top: 28, right: 10, bottom: 12, left: 35 },
};

const EMPTY_ARRAY = [];

function normalizeInvoicePageSizeKey(pageSize) {
  const key = String(pageSize || "a4")
    .trim()
    .toLowerCase();
  return key === "a5" ? "a5" : "a4";
}

/**
 * Accepts the UI values (`LETTERHEAD` / `WITHOUT_LETTERHEAD`) and the
 * get_invoice_config values (`WITH_LETTERHEAD` / `WITHOUT_LETTERHEAD`).
 * `WITHOUT` is checked first so it never matches the `WITH` prefix.
 */
function normalizeInvoicePrintType(printType) {
  const key = String(printType || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (key.startsWith("WITHOUT")) return "WITHOUT_LETTERHEAD";
  return key === "LETTERHEAD" || key === "WITH_LETTERHEAD"
    ? "LETTERHEAD"
    : "WITHOUT_LETTERHEAD";
}

function normalizeLetterheadTemplateType(type) {
  const key = String(type || "")
    .trim()
    .toLowerCase();
  if (key === "png") return "png";
  if (key === "pdf") return "pdf";
  if (key === "jpg" || key === "jpeg") return "jpg";
  return key || "";
}

/** Strip `data:...;base64,` prefix if present. */
function stripBase64Payload(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const marker = "base64,";
  const idx = s.indexOf(marker);
  if (s.startsWith("data:") && idx !== -1) {
    return s.slice(idx + marker.length).replace(/\s/g, "");
  }
  return s.replace(/\s/g, "");
}

function base64ToUint8Array(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

function sniffLetterheadTemplateType(b64Payload, declaredType) {
  if (b64Payload.startsWith("JVBERi0")) return "pdf";
  if (b64Payload.startsWith("iVBORw0KGgo")) return "png";
  if (b64Payload.startsWith("/9j/")) return "jpg";
  return normalizeLetterheadTemplateType(declaredType) || "jpg";
}

/**
 * Overlay letterhead template (jpg/png/pdf) behind each jsPDF page.
 * Returns PDF bytes (Uint8Array).
 */
async function composeInvoiceWithLetterheadTemplate(
  jsPdfDoc,
  templateBase64,
  templateType,
) {
  const payload = stripBase64Payload(templateBase64);
  if (!payload) {
    throw new Error("Empty letterhead template");
  }
  const kind = sniffLetterheadTemplateType(payload, templateType);
  const srcBytes = jsPdfDoc.output("arraybuffer");
  const newDoc = await PDFDocument.create();
  const srcDoc = await PDFDocument.load(srcBytes);
  const pageCount = srcDoc.getPageCount();
  const templateBytes = base64ToUint8Array(payload);

  if (kind === "pdf") {
    for (let i = 0; i < pageCount; i += 1) {
      const [embeddedSrcPage] = await newDoc.embedPdf(srcBytes, [i]);
      const [embeddedTemplatePage] = await newDoc.embedPdf(templateBytes, [0]);
      const page = newDoc.addPage([
        embeddedSrcPage.width,
        embeddedSrcPage.height,
      ]);
      page.drawPage(embeddedTemplatePage, {
        x: 0,
        y: 0,
        width: embeddedSrcPage.width,
        height: embeddedSrcPage.height,
      });
      page.drawPage(embeddedSrcPage, {
        x: 0,
        y: 0,
        width: embeddedSrcPage.width,
        height: embeddedSrcPage.height,
      });
    }
  } else {
    const embeddedImage =
      kind === "png"
        ? await newDoc.embedPng(templateBytes)
        : await newDoc.embedJpg(templateBytes);
    for (let i = 0; i < pageCount; i += 1) {
      const [embeddedSrcPage] = await newDoc.embedPdf(srcBytes, [i]);
      const page = newDoc.addPage([
        embeddedSrcPage.width,
        embeddedSrcPage.height,
      ]);
      page.drawImage(embeddedImage, {
        x: 0,
        y: 0,
        width: embeddedSrcPage.width,
        height: embeddedSrcPage.height,
      });
      page.drawPage(embeddedSrcPage, {
        x: 0,
        y: 0,
        width: embeddedSrcPage.width,
        height: embeddedSrcPage.height,
      });
    }
  }

  return newDoc.save();
}

function downloadPdfBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function openPdfBytesInNewTab(bytes) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(`${url}#zoom=page-fit`, "_blank");
}


/** Map API `margins[]` → `{ a4: {top,right,bottom,left}, a5: {...} }`. */
function parseInvoiceMarginsByPageSize(marginsList) {
  const map = {};
  if (!Array.isArray(marginsList)) return map;
  marginsList.forEach((m) => {
    if (!m) return;
    const key = normalizeInvoicePageSizeKey(m.pageSize);
    map[key] = {
      top: Number(m.top) || 0,
      right: Number(m.right) || 0,
      bottom: Number(m.bottom) || 0,
      left: Number(m.left) || 0,
    };
  });
  return map;
}

function getNoLetterheadMarginsMm(paperSize, marginsByPage) {
  const key = normalizeInvoicePageSizeKey(paperSize);
  return (
    (marginsByPage && marginsByPage[key]) ||
    INVOICE_PRINT_NO_LETTERHEAD_MARGIN_MM[key] ||
    INVOICE_PRINT_NO_LETTERHEAD_MARGIN_MM.a4
  );
}

/**
 * Apply API/fallback WITHOUT_LETTERHEAD margins onto PDF layout constants.
 * With-letterhead callers must not use this — leave layout as getInvoicePdfPageLayout().
 * Recalculates addrWrap/centerWrap and keeps left/right header columns from overlapping.
 */
function applyWithoutLetterheadLayoutOverrides(layout, margins) {
  const {
    PAGE_WIDTH,
    labelX,
    valueX,
    rightX,
    valueOffset,
    sideMargin,
    narrow,
  } = layout;

  const dx = margins.left - sideMargin;
  const nextLabelX = labelX + dx;
  const nextValueX = valueX + dx;

  // Keep original right-block width, re-anchor from the configured right margin.
  const rightBlockOuter = PAGE_WIDTH - sideMargin - rightX;
  let nextRightX = PAGE_WIDTH - margins.right - rightBlockOuter;

  const colGap = narrow ? 3 : 4;
  const minAddrWrap = narrow ? 20 : 36;
  const minCenterContent = narrow ? valueOffset + 20 : valueOffset + 48;
  const rightEdge = PAGE_WIDTH - margins.right;

  // Ensure left address column cannot collide with the right meta block.
  const minRightXForLeft = nextValueX + minAddrWrap + colGap;
  if (nextRightX < minRightXForLeft) {
    nextRightX = minRightXForLeft;
  }
  // Keep enough room for center values inside the right margin.
  const maxRightX = rightEdge - minCenterContent;
  if (nextRightX > maxRightX) {
    nextRightX = Math.max(nextValueX + colGap + 12, maxRightX);
  }

  const nextAddrWrap = Math.max(
    narrow ? 16 : 24,
    nextRightX - nextValueX - colGap,
  );
  const nextCenterWrap = Math.max(
    narrow ? 16 : 28,
    rightEdge - (nextRightX + valueOffset) - 2,
  );

  return {
    ...layout,
    labelX: nextLabelX,
    valueX: nextValueX,
    rightX: nextRightX,
    addrWrap: nextAddrWrap,
    centerWrap: nextCenterWrap,
    sideMargin: margins.left,
    contentStartY: margins.top,
    tableLeftMargin: margins.left,
    tableRightMargin: margins.right,
    noLetterheadTop: margins.top,
    noLetterheadBottom: margins.bottom,
    amountWordsRightEdge: narrow
      ? nextRightX - 4
      : rightEdge - 2,
  };
}

/**
 * When invoice payloads include overlapping line-item arrays (e.g. UI `itemsList` with empty
 * `itemName` vs API `items` with `item_name` on each row), pick the array with the most
 * resolved service names.
 */
function countResolvedServiceNames(rows) {
  if (!Array.isArray(rows)) return -1;
  let c = 0;
  for (const row of rows) {
    const raw =
      row?.item_name ??
      row?.itemName ??
      row?.service_name ??
      row?.name ??
      "";
    const t = String(raw).trim();
    if (t && t !== "--" && t !== "-") c += 1;
  }
  return c;
}

function pickInvoiceLineItems(data) {
  if (!data) return [];
  const candidates = [
    data.itemsList,
    data.invoice_items,
    data.invoiceItems,
    data.items,
  ].filter((a) => Array.isArray(a) && a.length > 0);
  if (candidates.length === 0) return [];
  let best = candidates[0];
  let bestScore = countResolvedServiceNames(best);
  for (let i = 1; i < candidates.length; i += 1) {
    const arr = candidates[i];
    const sc = countResolvedServiceNames(arr);
    if (sc > bestScore) {
      best = arr;
      bestScore = sc;
    }
  }
  return best;
}

/** Non-refund rows that count toward settlement tables and paid-amount sums. */
function isInvoiceSettlementPayment(payment) {
  const paymentType = String(payment?.payment_type || "").toUpperCase();
  if (paymentType === "REFUND") return false;
  const status = String(payment?.status || "").toUpperCase();
  const blocked = new Set([
    "FAILED",
    "CANCELLED",
    "VOID",
    "REJECTED",
    "REVERSED",
  ]);
  if (blocked.has(status)) return false;
  if (
    status === "" ||
    status === "SUCCESS" ||
    status === "PARTIALLY PAID" ||
    status === "PARTIALLY_PAID" ||
    status === "PAID" ||
    status === "PENDING" ||
    status === "COMPLETED"
  ) {
    return true;
  }
  if (
    paymentType === "PARTIAL" ||
    paymentType === "FULL" ||
    paymentType === "PARTIAL PAYMENT"
  ) {
    return true;
  }
  return false;
}

function sortSettlementPayments(list) {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const sa = Number(a?.receipt_sequence);
    const sb = Number(b?.receipt_sequence);
    if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) {
      return sa - sb;
    }
    const ta = new Date(a?.created_at || 0).getTime();
    const tb = new Date(b?.created_at || 0).getTime();
    if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb;
    return Number(a?.payment_id ?? 0) - Number(b?.payment_id ?? 0);
  });
}

/** Latest row after `sortSettlementPayments` (highest sequence / last created). */
function pickLatestSettlementPayment(sortedSettlementList) {
  if (!Array.isArray(sortedSettlementList) || sortedSettlementList.length === 0) {
    return null;
  }
  return sortedSettlementList[sortedSettlementList.length - 1];
}

/** Signatory = creator of the first payment (invoice) or of the receipt's own, latest payment (receipt). */
function getSignatoryName(mode, sortedSettlementList) {
  if (!Array.isArray(sortedSettlementList) || sortedSettlementList.length === 0) {
    return "";
  }
  const payment =
    mode === "receipt"
      ? pickLatestSettlementPayment(sortedSettlementList)
      : sortedSettlementList[0];
  return String(payment?.created_by_name ?? "").trim();
}

function displayPayMode(mode) {
  const t = mode == null ? "" : String(mode).trim();
  return t ? t : "N/A";
}

function displayTxnRef(ref) {
  const t = ref == null ? "" : String(ref).trim();
  return t ? t : "N/A";
}

function buildServiceNameByIdMap(serviceMaster = []) {
  const map = new Map();
  if (!Array.isArray(serviceMaster)) return map;
  serviceMaster.forEach((s) => {
    const key = s?.medical_service_id ?? s?.id;
    const value = s?.name ?? s?.item_name ?? "";
    if (key != null && String(value).trim()) {
      map.set(String(key), String(value).trim());
    }
  });
  return map;
}

function enrichLineItemsWithServiceNames(rows, serviceMap) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const existingName = String(
      row?.item_name ?? row?.itemName ?? row?.service_name ?? "",
    ).trim();
    if (existingName && existingName !== "--" && existingName !== "-") {
      return row;
    }
    const serviceId = row?.items ?? row?.item_id ?? row?.service_id;
    const resolved =
      serviceId != null ? serviceMap.get(String(serviceId)) || "" : "";
    if (!resolved) return row;
    return {
      ...row,
      item_name: resolved,
      itemName: resolved,
    };
  });
}

/**
 * jsPDF format for invoice PDFs. A5 uses explicit mm dimensions so exports are never A4-sized.
 * A4 keeps the string "a4" — behavior unchanged from before.
 */
function getInvoicePdfJsFormat(paperSize) {
  return paperSize === "a5" ? INVOICE_PDF_A5_MM : "a4";
}

function measureInvoicePdfFooter(doc, paperSize) {
  if (paperSize === "a5") {
    return {
      lines: [],
      lineH: 0,
      logoGap: 0,
      logoMaxH: 0,
      padTop: 0,
      padBot: 0,
      bandH: 0,
      narrow: true,
    };
  }
  const pageW = doc.internal.pageSize.getWidth();
  const textMaxW = Math.max(50, pageW - 22);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(INVOICE_PDF_FOOTER_TEXT, textMaxW);
  const lineH = 3.5;
  const logoGap = 1.2;
  const logoMaxH = 7;
  const padTop = 2;
  const padBot = 2;
  // Text uses baselines at padTop+lineH .. padTop+lines.length*lineH; logo sits just below last baseline.
  const bandH = padTop + lines.length * lineH + logoGap + logoMaxH + padBot;
  return {
    lines,
    lineH,
    logoGap,
    logoMaxH,
    padTop,
    padBot,
    bandH,
    narrow: false,
  };
}

function drawInvoicePdfFooter(doc, hlthtekLogoSrc, layout) {
  const pageHeight = doc.internal.pageSize.height;
  const pageW = doc.internal.pageSize.getWidth();
  const centerX = pageW / 2;
  const { lines, lineH, logoGap, logoMaxH, padTop, bandH, narrow } = layout;
  const footerTop = pageHeight - bandH;
  doc.setFillColor(33, 205, 192);
  doc.rect(0, footerTop, pageW, bandH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(narrow ? 7 : 10);
  let yText = footerTop + padTop + lineH;
  lines.forEach((line) => {
    doc.text(line, centerX, yText, { align: "center" });
    yText += lineH;
  });
  // After loop yText points *past* the last baseline; logo must start after last line, not an extra lineH down.
  const logoY = yText - lineH + logoGap;
  try {
    const imgProps = doc.getImageProperties(hlthtekLogoSrc);
    const maxWidth = narrow ? 25 : 40;
    const ratio = imgProps.width / imgProps.height;
    let width = maxWidth;
    let height = width / ratio;
    if (height > logoMaxH) {
      height = logoMaxH;
      width = height * ratio;
    }
    doc.addImage(
      hlthtekLogoSrc,
      "PNG",
      centerX - width / 2,
      logoY,
      width,
      height,
    );
  } catch (e) {
    doc.setFontSize(narrow ? 6 : 9);
    doc.text("Hlthtek", centerX, logoY + (narrow ? 1.5 : 2.5), {
      align: "center",
    });
  }
}

/** Layout constants for invoice PDF (A4 vs A5 portrait). */
function getInvoicePdfPageLayout(doc, paperSize) {
  const PAGE_WIDTH = doc.internal.pageSize.getWidth();
  const PAGE_HEIGHT = doc.internal.pageSize.getHeight();
  const narrow = paperSize === "a5";
  // A5 left labels at x≈6; values start after label width. A4 keeps original layout.
  const labelX = narrow ? 6 : 10;
  const valueX = narrow ? 32 : 28;
  // Right column must start after left block.
  const rightX = narrow ? 78 : 120;
  const valueOffset = narrow ? 13 : 16;
  const sideMargin = narrow ? 5 : 10;
  const addrWrap = narrow ? Math.max(28, rightX - valueX - 4) : 70;
  const centerWrap = narrow
    ? Math.max(20, PAGE_WIDTH - sideMargin - (rightX + valueOffset) - 2)
    : 65;
  // Header band height: A5 uses compact 15mm header vs A4's 24mm (enlarged to fit bigger logo)
  const headerBandH = narrow ? 15 : 24;
  // Vertical spacing between patient info lines
  const infoLineGap = narrow ? 2.5 : 3.5;
  // Y start after header
  const contentStartY = narrow ? 19 : 32;
  // Title-to-info gap
  const titleGap = narrow ? 4 : 8;
  return {
    PAGE_WIDTH,
    PAGE_HEIGHT,
    narrow,
    labelX,
    valueX,
    rightX,
    valueOffset,
    addrWrap,
    centerWrap,
    headerBandH,
    infoLineGap,
    contentStartY,
    titleGap,
    tableFont: narrow ? 4.5 : 7,
    tableHeadFont: narrow ? 5 : 8,
    paymentFont: narrow ? 5.5 : 9,
    headerTitleFont: narrow ? 8 : 15,
    sideMargin,
    /** Keep "amount in words" left of the totals column on narrow pages. */
    amountWordsRightEdge: narrow ? rightX - 4 : PAGE_WIDTH - sideMargin - 2,
    /** Table cell padding for A5 vs A4 */
    tableCellPadding: narrow
      ? { top: 1, bottom: 1, left: 1, right: 1 }
      : { top: 2, bottom: 2, left: 2, right: 2 },
    /** Header logo max dimensions (enlarged to match prescription-style branding) */
    headerLogoMaxW: narrow ? 36 : 55,
    headerLogoMaxH: narrow ? 11 : 18,
    /** Info section font size */
    infoFontSize: narrow ? 5.5 : 7,
    /** Title font size */
    titleFontSize: narrow ? 7 : 10,
  };
}

/** jsPDF-AutoTable: line items — SAC + Qty centered; numeric cols right; description left (head + body). */
function alignInvoiceLineItemsCell(data) {
  if (data.section !== "head" && data.section !== "body") return;
  const i = data.column.index;
  if (i === 0) data.cell.styles.halign = "left";
  else if (i === 1 || i === 2) data.cell.styles.halign = "center";
  else data.cell.styles.halign = "right";
}

/**
 * "Received with thanks: " + amount in words. A4: 10pt, usual wrap.
 * A5: step font down so the amount stays on the same line as the prefix when it fits.
 */
function getThanksInWordsLayout(
  doc,
  receivedText,
  amountText,
  thanksRightEdge,
  narrow,
) {
  if (!narrow) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const textWidth = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxAmountWidth = Math.max(26, thanksRightEdge - (10 + textWidth));
    const wrappedAmount = doc.splitTextToSize(amountText, maxAmountWidth);
    return {
      thanksFs: 10,
      textWidth,
      wrappedAmount,
      lineHeight: 5,
    };
  }
  let best = {
    thanksFs: 5,
    textWidth: 0,
    wrappedAmount: [amountText],
    lineHeight: 4,
  };
  for (let fs = 10; fs >= 5; fs -= 0.5) {
    doc.setFontSize(fs);
    doc.setFont("helvetica", "normal");
    const tw = doc.getTextWidth(receivedText);
    doc.setFont("helvetica", "bold");
    const maxW = Math.max(14, thanksRightEdge - (10 + tw));
    const wrapped = doc.splitTextToSize(amountText, maxW);
    const lh = fs <= 6 ? 3.8 : fs <= 7.5 ? 4.2 : fs <= 9 ? 4.6 : 5;
    best = {
      thanksFs: fs,
      textWidth: tw,
      wrappedAmount: wrapped,
      lineHeight: lh,
    };
    if (wrapped.length <= 1) {
      break;
    }
  }
  return best;
}

const ViewInvoice = ({
  mode = "invoice",
  /** When true, hide footer paper size + PDF buttons + Go home (appointment preview dialog only). */
  externalPdfToolbar = false,
  /** Registers PDF UI API for an external toolbar (e.g. dialog header). */
  onPdfToolbarApiReady,
}) => {
  const location = useLocation();
  const history = useHistory();
  const brandingData = useContext(BrandingContext);

  //only props / route state
  const { data, serviceMaster = EMPTY_ARRAY, patientMaster = EMPTY_ARRAY } = location.state || {};
  const [itemState, setItemState] = useState([]);
  const [fetchedPatient, setFetchedPatient] = useState(null);
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
  /** PDF / print page format — overridden by get_invoice_config defaultPageSize. */
  const [invoicePaperSize, setInvoicePaperSize] = useState("a4");
  /** LETTERHEAD | WITHOUT_LETTERHEAD — overridden by get_invoice_config defaultPrintType. */
  const [invoicePrintType, setInvoicePrintType] = useState(
    "WITHOUT_LETTERHEAD",
  );
  /** WITHOUT_LETTERHEAD / template-letterhead margins by page size (`a4` / `a5`). */
  const [noLetterheadMarginsByPage, setNoLetterheadMarginsByPage] = useState(
    () => ({ ...INVOICE_PRINT_NO_LETTERHEAD_MARGIN_MM }),
  );
  /** Optional facility letterhead background (base64) from get_invoice_config. */
  const [letterheadTemplate, setLetterheadTemplate] = useState(null);
  const [letterheadTemplateType, setLetterheadTemplateType] = useState("jpg");
  const { userInfo, loading: permissionLoading } = usePermissions();
  const REACT_APP_BACKEND_ROUTE_URL =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "";
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  //  const userRole = userInfo?.role_permission?.[0]?.role || "";
  // const userRole = "billingadmin";
  // const isFacilityAdmin = userRole === "billingadmin";
  const userRole = userInfo?.role_permission?.[0]?.role || "";
  const isBillingAdmin = userRole?.toLowerCase() === "billingadmin";
  const isFacilityAdmin = userRole === "facility user";

  /* =========================================================
      HELPERS
  ========================================================= */
  const safeNumber = (val) => {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };

  const buildFacilityAddress = (info) => {
    const parts = [
      info?.facility_address_line_1,
      info?.facility_address_line_2,
      info?.facility_address_line_3,
      info?.city,
      info?.state,
      info?.pincode ? `PIN: ${info.pincode}` : null,
    ];
    return parts.filter((x) => x && String(x).trim() !== "").join(", ");
  };

  /** Facility contact number from get_logged_in_user_info; line is hidden when empty. */
  const facilityMobile = String(userInfo?.facility_mobile ?? "").trim();

  const formatMoney = (val) => safeNumber(val).toFixed(2);
  const formatReferredBy = (value) => {
    // Stored as a comma-separated list; show it with a space after each comma.
    const normalized =
      value == null
        ? ""
        : String(value)
            .split(",")
            .map((name) => name.trim())
            .filter(Boolean)
            .join(", ");
    return normalized || "N/A";
  };

  /** Backend key is `note` (singular); locally-built invoice objects use `notes`. */
  const getInvoiceNotesText = (d) =>
    String(d?.notes ?? d?.note ?? "").trim();

  /** Always renders in IST, regardless of the viewing machine's own timezone. */
  const IST_TIME_ZONE = "Asia/Kolkata";

  /**
   * Backend sends timestamps like "created_at" as naive strings (no `Z` / offset)
   * that are actually UTC — e.g. "2026-09-30T03:21:00". Left alone, the browser's
   * Date parser reads that as UTC (per spec) *or* local time depending on the
   * exact format, which is why times were off. Force UTC parsing whenever a time
   * component is present but no timezone marker was given; leave already-tagged
   * (`Z` / `+hh:mm`) or date-only strings untouched.
   */
  const parseBackendTimestamp = (raw) => {
    if (raw instanceof Date) return raw;
    const s = String(raw).trim();
    if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) {
      return new Date(`${s.replace(" ", "T")}Z`);
    }
    return new Date(s);
  };

  const formatDate = (raw) => {
    if (!raw || raw === "N/A") return "N/A";
    try {
      const d = parseBackendTimestamp(raw);
      if (isNaN(d.getTime())) return String(raw);
      return d.toLocaleDateString("en-GB", {
        timeZone: IST_TIME_ZONE,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return String(raw);
    }
  };

  const formatDateTime = (raw) => {
    if (!raw || raw === "N/A") return "N/A";
    try {
      const d = parseBackendTimestamp(raw);
      if (isNaN(d.getTime())) return String(raw);
      const time = d.toLocaleTimeString("en-IN", {
        timeZone: IST_TIME_ZONE,
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${formatDate(d)}, ${time}`;
    } catch (e) {
      return String(raw);
    }
  };

  const formatAgeGenderLine = (p, d) => {
    // The invoice age is a bill-time snapshot; patient master age changes over time.
    const age = d?.patient_age ?? d?.age ?? p?.age ?? p?.age_years;
    const genderRaw = p?.gender ?? p?.sex ?? d?.gender ?? "N/A";
    const genderStr =
      genderRaw != null && String(genderRaw).trim() !== ""
        ? String(genderRaw).trim()
        : "N/A";
    const ageValue = age == null ? "" : String(age).trim();
    const ageStr =
      ageValue && ageValue.toLowerCase() !== "n/a"
        ? /\byears?\b/i.test(ageValue)
          ? ageValue
          : `${ageValue} Years`
        : "N/A";
    return `${ageStr} / ${genderStr}`;
  };

  const formatContactNumbers = (p, d) => {
    const primary =
      p?.phone_no ||
      p?.phone ||
      p?.contact_no ||
      p?.mobile_no ||
      d?.phone ||
      d?.phone_no ||
      "";
    const alternateRaw =
      p?.alternate_phone ??
      p?.alternate_phone_no ??
      p?.alternate_contact_no ??
      p?.secondary_phone ??
      p?.emergency_phone ??
      p?.emergency_contact ??
      p?.phone_no_2 ??
      p?.mobile_no_2 ??
      p?.alternate_mobile ??
      d?.alternate_phone ??
      d?.alternate_phone_no ??
      "";
    const alt = alternateRaw != null ? String(alternateRaw).trim() : "";
    const pri = primary != null ? String(primary).trim() : "";
    if (!pri && !alt) return "N/A";
    if (pri && alt && alt !== pri) return `${pri} / ${alt}`;
    return pri || alt;
  };

  const getInvoiceDate = (d) => {
    return (
      d?.invoiceDate ||
      d?.invoice_date ||
      d?.invoice_date_time ||
      d?.date ||
      d?.created_at ||
      d?.createdAt ||
      d?.due_date ||
      d?.dueDate ||
      null
    );
  };

  // invoice_date is often date-only or midnight (shows as 05:30 am IST); use a real timestamp instead.
  const hasRealTime = (v) =>
    !!v && /\d{2}:\d{2}/.test(String(v)) && !/[ T]00:00(:00)?/.test(String(v));
  const getInvoiceDateTime = (d, payments) => {
    const invDate = getInvoiceDate(d);
    const firstPaymentAt = Array.isArray(payments) ? payments[0]?.created_at : null;
    const withTime = [
      invDate,
      d?.invoice_date_time,
      d?.created_at,
      d?.createdAt,
      firstPaymentAt,
    ].find(hasRealTime);
    return withTime ? formatDateTime(withTime) : formatDate(invDate);
  };

  const getFinalTotal = (d) => {
    return safeNumber(
      d?.total ||
        d?.total_amount ||
        d?.totalAmount ||
        d?.payable_amount ||
        d?.amount ||
        d?.subtotal ||
        0,
    );
  };

  /** Bill = sum of line gross; discount = line + bill-level; patient payable = bill − discount. */
  const getInvoiceSummaryFromItems = (normalizedItems, invoiceData) => {
    const sumGross = normalizedItems.reduce(
      (s, i) => s + safeNumber(i.gross),
      0,
    );
    const sumLineDiscount = normalizedItems.reduce(
      (s, i) => s + safeNumber(i.discount_amount),
      0,
    );
    // Bill-level % applies on net after line discounts (matches backend update_invoice_discount)
    const sumLineNet = Math.max(sumGross - sumLineDiscount, 0);

    const invoiceDiscPct = safeNumber(invoiceData?.discount ?? 0);
    const invoiceDiscAmtFromApi = safeNumber(
      invoiceData?.discount_amount ??
        invoiceData?.total_discount ??
        invoiceData?.invoice_discount_amount ??
        0,
    );

    const apiTotal = safeNumber(invoiceData?.total_amount);
    const taxPct = safeNumber(invoiceData?.tax);

    let invoiceLevelDiscount = invoiceDiscAmtFromApi;
    if (!(invoiceLevelDiscount > 0) && invoiceDiscPct > 0) {
      invoiceLevelDiscount = (sumLineNet * invoiceDiscPct) / 100;
    }

    // Percent is stored to 3 decimals (1.916). Applying it prints ₹100.02
    // even when the saved bill total is ₹5,120, which means the discount is ₹100.00.
    if (apiTotal > 0) {
      const netBeforeTax =
        taxPct !== 0 ? apiTotal / (1 + taxPct / 100) : apiTotal;
      if (sumLineNet + 0.001 >= netBeforeTax) {
        invoiceLevelDiscount = sumLineNet - netBeforeTax;
      }
    }

    invoiceLevelDiscount = Number(Math.max(0, invoiceLevelDiscount).toFixed(2));
    const totalDiscountAmount = Number(
      (sumLineDiscount + invoiceLevelDiscount).toFixed(2),
    );
    const computedPayable = Math.max(sumGross - totalDiscountAmount, 0);
    const patientPayable = apiTotal > 0 ? apiTotal : computedPayable;
    const invoiceDiscountPercent =
      sumLineNet > 0
        ? (invoiceLevelDiscount / sumLineNet) * 100
        : invoiceDiscPct;

    return {
      billAmount: sumGross,
      totalDiscountAmount,
      patientPayable,
      sumLineDiscount,
      invoiceLevelDiscount,
      invoiceDiscountPercent,
    };
  };

  /** Line columns stay as billed line discounts. Bill discount is shown only in the summary. */
  const mergeDisplayDiscounts = (normalizedItems) =>
    (normalizedItems || []).map((item) => ({ ...item }));

  /* =========================================================
      SERVICE MASTER LOOKUP
  ========================================================= */
  const getServiceNameById = (id) => {
    if (!id) return "--";
    const found = serviceMaster?.find(
      (s) => s?.medical_service_id === id || s?.id === id,
    );
    return found?.name || found?.item_name || "--";
  };

  /* =========================================================
      PATIENT MASTER LOOKUP
  ========================================================= */
  const patientId = data?.patient_id || data?.patientId;
  const patient = useMemo(() => {
    if (!patientId) return null;
    return patientMaster?.find((p) => p?.patient_id === patientId) || null;
  }, [patientId, patientMaster]);

  // When coming from invoice list we don't have patientMaster; fetch patient by id
  useEffect(() => {
    if (
      !data ||
      !patientId ||
      patient ||
      !authToken ||
      !REACT_APP_BACKEND_ROUTE_URL
    )
      return;
    let cancelled = false;
    const fetchPatient = async () => {
      try {
        const res = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
          {
            filter_type: "patient_id",
            filter_details: { patient_id: patientId },
            multipleResults: true,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (cancelled) return;
        if (
          res?.data?.status === "Success" &&
          Array.isArray(res?.data?.patient_details)
        ) {
          const first = res.data.patient_details[0];
          if (first) setFetchedPatient(first);
        }
      } catch (e) {
        if (!cancelled) console.error("ViewInvoice: fetch patient failed", e);
      }
    };
    fetchPatient();
    return () => {
      cancelled = true;
    };
  }, [data, patientId, patient, authToken, REACT_APP_BACKEND_ROUTE_URL]);

  const displayPatient = patient || fetchedPatient;

  const normalizeItem = (item = {}) =>
    normalizeInvoiceLineItem(item, {
      resolveItemName: getServiceNameById,
      invoiceContext: data,
      lineCount: itemState.length,
      paymentAmountHint:
        getInvoicePaymentTotal(data) > 0 ? undefined : data?.amount_paid,
    });

  /* =========================================================
      LOAD ITEMS
  ========================================================= */
  useEffect(() => {
    if (!sessionStorage.getItem("accessToken")) {
      history.push("/login");
      return;
    }

    if (!data) {
      toast.error("Invoice data not found. Redirecting...");
      history.push("/doctor/invoice");
      return;
    }

    const items = pickInvoiceLineItems(data);
    const mergedItems = enrichLineItemsFromInvoiceTotals(
      enrichLineItemsWithServiceNames(
        items,
        buildServiceNameByIdMap(serviceMaster),
      ),
      data,
    );

    setItemState(Array.isArray(mergedItems) ? mergedItems : []);
  }, [data, history, serviceMaster]);

  /* =========================================================
      INVOICE PDF CONFIG (page size, print type, WITHOUT_LETTERHEAD margins)
  ========================================================= */
  useEffect(() => {
    if (!authToken || !REACT_APP_BACKEND_ROUTE_URL) return;

    let cancelled = false;
    const fetchInvoiceConfig = async () => {
      try {
        const res = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}get_invoice_config/`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (cancelled) return;
        if (res?.data?.status !== "Success") return;
        const cfg = res.data.invoice_template_config || {};
        const parsedMargins = parseInvoiceMarginsByPageSize(cfg.margins);
        if (Object.keys(parsedMargins).length > 0) {
          setNoLetterheadMarginsByPage({
            ...INVOICE_PRINT_NO_LETTERHEAD_MARGIN_MM,
            ...parsedMargins,
          });
        }
        if (cfg.defaultPageSize) {
          setInvoicePaperSize(
            normalizeInvoicePageSizeKey(cfg.defaultPageSize),
          );
        }
        if (cfg.defaultPrintType) {
          setInvoicePrintType(normalizeInvoicePrintType(cfg.defaultPrintType));
        }
        const templateRaw = cfg.letterheadTemplate;
        if (templateRaw != null && String(templateRaw).trim() !== "") {
          setLetterheadTemplate(String(templateRaw).trim());
          setLetterheadTemplateType(
            normalizeLetterheadTemplateType(cfg.letterheadTemplateType) ||
              "jpg",
          );
        } else {
          setLetterheadTemplate(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("ViewInvoice: get_invoice_config failed", e);
        }
      }
    };
    fetchInvoiceConfig();
    return () => {
      cancelled = true;
    };
  }, [authToken, REACT_APP_BACKEND_ROUTE_URL]);

  /* =========================================================
      BRANDING
  ========================================================= */
  const collateralImageItem = brandingData?.find(
    (item) => item.name === "collateral_image",
  );
  const collateralImage = collateralImageItem?.content || null;

  /* =========================================================
      PDF HELPERS
  ========================================================= */
  const numberToWords = (num) => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100)
        return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
      if (n < 1000)
        return (
          a[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + inWords(n % 100) : "")
        );
      if (n < 100000)
        return (
          inWords(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + inWords(n % 1000) : "")
        );
      // Indian numbering system: Lakh (1,00,000)
      if (n < 10000000)
        return (
          inWords(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + inWords(n % 100000) : "")
        );
      // Indian numbering system: Crore (1,00,00,000)
      if (n < 1000000000)
        return (
          inWords(Math.floor(n / 10000000)) +
          " Crore" +
          (n % 10000000 ? " " + inWords(n % 10000000) : "")
        );
      return "";
    };

    return inWords(num);
  };

  /* =========================================================
      DOWNLOAD PDF
  ========================================================= */
  const handleDownload = async (withLetterhead = true) => {
    if (!data) return;

    const hasLetterheadTemplate = Boolean(
      letterheadTemplate && String(letterheadTemplate).trim(),
    );
    const useTemplateLetterhead = withLetterhead && hasLetterheadTemplate;
    const useDrawnLetterhead = withLetterhead && !hasLetterheadTemplate;

    const normalizedItems = itemState.map((it) => normalizeItem(it));
    if (!normalizedItems.length) {
      toast.error("No items found!");
      return;
    }

    const summaryPdf = getInvoiceSummaryFromItems(normalizedItems, data);
    const displayItemsPdf = mergeDisplayDiscounts(normalizedItems);
    const billAmtPdf =
      summaryPdf.billAmount > 0
        ? summaryPdf.billAmount
        : safeNumber(getFinalTotal(data));
    const discAmtPdf =
      summaryPdf.billAmount > 0 ? summaryPdf.totalDiscountAmount : 0;
    const patientPayablePdf =
      summaryPdf.billAmount > 0
        ? summaryPdf.patientPayable
        : safeNumber(getFinalTotal(data));

    const doc = new jsPDF("p", "mm", getInvoicePdfJsFormat(invoicePaperSize));
    let {
      PAGE_WIDTH,
      labelX,
      valueX,
      rightX,
      valueOffset,
      addrWrap,
      centerWrap,
      tableFont,
      tableHeadFont,
      paymentFont,
      headerTitleFont,
      sideMargin,
      narrow,
      amountWordsRightEdge,
      headerBandH,
      infoLineGap,
      contentStartY,
      titleGap,
      tableCellPadding,
      headerLogoMaxW,
      headerLogoMaxH,
      infoFontSize,
      titleFontSize,
    } = getInvoicePdfPageLayout(doc, invoicePaperSize);

    // Content inset margins: WITHOUT_LETTERHEAD + LETTERHEAD with background template.
    let tableLeftMargin = sideMargin;
    let tableRightMargin = sideMargin + 2;
    let noLetterheadTop = narrow ? 20 : 30;
    let noLetterheadBottom = narrow ? 8 : 22;
    if (!useDrawnLetterhead) {
      const nlMargins = getNoLetterheadMarginsMm(
        invoicePaperSize,
        noLetterheadMarginsByPage,
      );
      const overridden = applyWithoutLetterheadLayoutOverrides(
        {
          PAGE_WIDTH,
          labelX,
          valueX,
          rightX,
          valueOffset,
          addrWrap,
          centerWrap,
          tableFont,
          tableHeadFont,
          paymentFont,
          headerTitleFont,
          sideMargin,
          narrow,
          amountWordsRightEdge,
          headerBandH,
          infoLineGap,
          contentStartY,
          titleGap,
          tableCellPadding,
          headerLogoMaxW,
          headerLogoMaxH,
          infoFontSize,
          titleFontSize,
        },
        nlMargins,
      );
      labelX = overridden.labelX;
      valueX = overridden.valueX;
      rightX = overridden.rightX;
      addrWrap = overridden.addrWrap;
      centerWrap = overridden.centerWrap;
      sideMargin = overridden.sideMargin;
      amountWordsRightEdge = overridden.amountWordsRightEdge;
      contentStartY = overridden.contentStartY;
      tableLeftMargin = overridden.tableLeftMargin;
      tableRightMargin = overridden.tableRightMargin;
      noLetterheadTop = overridden.noLetterheadTop;
      noLetterheadBottom = overridden.noLetterheadBottom;
    }

    const pdfPaymentsList = Array.isArray(data?.payment_details)
      ? data.payment_details
      : Array.isArray(data?.payments)
        ? data.payments
        : data?.payment
          ? [data.payment]
          : [];
    const settlementPdfPayments = sortSettlementPayments(
      pdfPaymentsList.filter(isInvoiceSettlementPayment),
    );
    const pdfPayment =
      settlementPdfPayments[0] || data?.payment || data?.payments?.[0] || {};
    const payment_date = data?.payment_date || pdfPayment?.created_at || "N/A";
    const receipt_no = data?.receipt_no || pdfPayment?.receipt_number || "N/A";
    const payment_mode =
      data?.payment_mode || pdfPayment?.mode_of_payment || "N/A";
    const ref_no = data?.ref_no || pdfPayment?.transaction_ref_no || "N/A";
    let amount_paid = 0;
    if (settlementPdfPayments.length > 0) {
      amount_paid = settlementPdfPayments.reduce(
        (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
        0,
      );
    } else {
      amount_paid = safeNumber(
        data?.amount_paid ??
          data?.partial_payment ??
          pdfPayment?.payment_amount ??
          0,
      );
    }

    const latestPdfPayment = pickLatestSettlementPayment(settlementPdfPayments);
    const receiptNoForPdfRightHeader =
      mode === "receipt"
        ? (latestPdfPayment?.receipt_number ??
          data?.receipt_no ??
          pdfPayment?.receipt_number ??
          "N/A")
        : (data?.receipt_no || pdfPayment?.receipt_number || "N/A");
    const receiptDateForPdfRightHeader =
      latestPdfPayment?.created_at ??
      data?.payment_date ??
      pdfPayment?.created_at ??
      null;
    const receiptLineAmountPdf =
      mode === "receipt"
        ? latestPdfPayment
          ? safeNumber(latestPdfPayment?.payment_amount)
          : safeNumber(
              pdfPayment?.payment_amount ??
                data?.amount_paid ??
                data?.partial_payment ??
                0,
            )
        : amount_paid;

    const pdfDueAmount = safeNumber(patientPayablePdf - amount_paid);
    const pdfStatusLabel = pdfDueAmount > 0.01 ? "DUE" : "PAID";

    // ✅ Header logo (top right): collateral_image from branding, else fallback
    const headerLogoDataUrl = collateralImage
      ? `data:image/png;base64,${collateralImage}`
      : null;

    const invoiceFooterLayout = measureInvoicePdfFooter(doc, invoicePaperSize);

    // ===== HEADER (same output, global header for every page) =====
    // Important: function declaration use kiya because const hoisting problem hota hai
    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, PAGE_WIDTH, headerBandH, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(
        sessionStorage.getItem("facility_display_name") ||
          data?.facility_name ||
          displayPatient?.facility_name ||
          "Super Galaxy Health",
        narrow ? 6 : 10,
        narrow ? 8 : 12,
      );

      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(
            headerLogoDataUrl,
            "PNG",
            PAGE_WIDTH - sideMargin - width,
            narrow ? 2 : 3,
            width,
            height,
          );
        }
      } catch (e) {
        // fallback - ignore if logo not found
      }
    }

    function drawFooterTemplate() {
      // A5: no footer — skip entirely
      if (narrow) return;
      drawInvoicePdfFooter(doc, HlthtekFooterLogo, invoiceFooterLayout);
    }

    // ===== GLOBAL HOOK - drawn letterhead only (teal header). Template letterhead is composed later. =====
    const bottomMargin = useDrawnLetterhead
      ? narrow
        ? 8
        : Math.max(25, invoiceFooterLayout.bandH + 8)
      : noLetterheadBottom;
    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMargin,
        ...(useDrawnLetterhead ? {} : { top: noLetterheadTop }),
      },
      didDrawPage: useDrawnLetterhead
        ? function () {
            drawHeader();
            drawFooterTemplate();
          }
        : function () {},
    });

    const PAGE_HEIGHT = doc.internal.pageSize.height;
    let y = 15;

    if (useDrawnLetterhead) {
      drawHeader();
    }
    y = contentStartY;

    // TITLE & STATUS
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const titleText =
      mode === "receipt" ? "Bill Cum Receipt" : "Bill of Supply";
    doc.text(titleText, labelX, y);

    const statusStr = pdfStatusLabel;
    doc.setFontSize(narrow ? 6.5 : 9);
    if (statusStr === "PAID") {
      doc.setTextColor(6, 95, 70); // Green
    } else {
      doc.setTextColor(153, 27, 27); // Red (DUE)
    }
    doc.text(
      `Status: ${statusStr}`,
      labelX + doc.getTextWidth(titleText) + (narrow ? 3 : 5),
      y,
    );
    doc.setTextColor(0); // Reset to black
    y += titleGap;

    doc.setFontSize(infoFontSize);
    doc.setFont("helvetica", "normal");

    const patientY = y;

    doc.setFont("helvetica", "bold");
    doc.text("Name:", labelX, patientY);
    doc.setFont("helvetica", "normal");
    doc.text(
      data?.patient_name || data?.patientName || displayPatient?.name || "N/A",
      valueX,
      patientY,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Age/Gender:", labelX, patientY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatAgeGenderLine(displayPatient, data),
      valueX,
      patientY + infoLineGap,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Contact No:", labelX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatContactNumbers(displayPatient, data),
      valueX,
      patientY + infoLineGap * 2,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Address:", labelX, patientY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");

    const patientAddress =
      data?.address ||
      displayPatient?.address ||
      [
        displayPatient?.address_line,
        displayPatient?.locality,
        displayPatient?.city || displayPatient?.district,
        displayPatient?.state,
        displayPatient?.pincode,
      ]
        .filter(
          (v) =>
            v &&
            !["null", "undefined", "n/a"].includes(v.toString().toLowerCase()),
        )
        .join(", ");

    const finalAddress = patientAddress || "N/A";
    const addressLines = doc
      .splitTextToSize(finalAddress, addrWrap)
      .slice(0, 2);
    for (let i = 0; i < addressLines.length; i++) {
      doc.text(
        addressLines[i],
        valueX,
        patientY + infoLineGap * 3 + i * infoLineGap,
      );
    }

    const referredY =
      patientY + infoLineGap * 3 + addressLines.length * infoLineGap;
    doc.setFont("helvetica", "bold");
    doc.text("Referred by:", labelX, referredY);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatReferredBy(data?.referred_by || displayPatient?.referred_by),
      valueX,
      referredY,
    );

    const patIdValue =
      displayPatient?.patient_id ||
      data?.patient_id ||
      data?.patientId ||
      "N/A";

    // RIGHT BLOCK (rightX from page layout for A4 vs A5)
    const rightY = patientY;
    let facilityMobileY = 0;

    doc.setFont("helvetica", "bold");
    doc.text(mode === "receipt" ? "Receipt No:" : "Bill No:", rightX, rightY);
    doc.setFont("helvetica", "normal");
    doc.text(
      mode === "receipt" ? receiptNoForPdfRightHeader : data?.invoice_number || "N/A",
      rightX + valueOffset,
      rightY,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Pat ID:", rightX, rightY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(patIdValue, rightX + valueOffset, rightY + infoLineGap);

    if (settlementPdfPayments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(
        (mode === "receipt"
          ? formatDateTime(receiptDateForPdfRightHeader)
          : getInvoiceDateTime(data, settlementPdfPayments)) || "N/A",
        rightX + valueOffset,
        rightY + infoLineGap * 2,
      );
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const cName =
        data?.center ||
        data?.facility_name ||
        userInfo?.facility_name ||
        sessionStorage.getItem("facility_display_name") ||
        "N/A";
      const wrappedCName = doc.splitTextToSize(cName, centerWrap).slice(0, 1);
      doc.text(wrappedCName, rightX + valueOffset, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const cAddr =
        data?.center_address ||
        data?.facility_address ||
        buildFacilityAddress(userInfo) ||
        "N/A";
      const wrappedCAddr = doc.splitTextToSize(cAddr, centerWrap).slice(0, 2);
      doc.text(wrappedCAddr, rightX + valueOffset, rightY + infoLineGap * 4);
      if (facilityMobile) {
        facilityMobileY = rightY + infoLineGap * (4 + wrappedCAddr.length);
        doc.setFont("helvetica", "bold");
        doc.text("Mobile:", rightX, facilityMobileY);
        doc.setFont("helvetica", "normal");
        doc.text(facilityMobile, rightX + valueOffset, facilityMobileY);
      }
      y = rightY + infoLineGap * 6;
    } else {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(
        (mode === "receipt"
          ? formatDateTime(receiptDateForPdfRightHeader)
          : getInvoiceDateTime(data, settlementPdfPayments)) || "N/A",
        rightX + valueOffset,
        rightY + infoLineGap * 2,
      );

      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const centerName =
        data?.center ||
        data?.facility_name ||
        userInfo?.facility_name ||
        sessionStorage.getItem("facility_display_name") ||
        "N/A";
      const wrappedCenterName = doc
        .splitTextToSize(centerName, centerWrap)
        .slice(0, 1);
      doc.text(
        wrappedCenterName,
        rightX + valueOffset,
        rightY + infoLineGap * 3,
      );

      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const centerAddrArr =
        data?.center_address ||
        data?.facility_address ||
        buildFacilityAddress(userInfo) ||
        "N/A";
      const wrappedCenterAddr = doc
        .splitTextToSize(centerAddrArr, centerWrap)
        .slice(0, 2);
      doc.text(
        wrappedCenterAddr,
        rightX + valueOffset,
        rightY + infoLineGap * 4,
      );
      if (facilityMobile) {
        facilityMobileY = rightY + infoLineGap * (4 + wrappedCenterAddr.length);
        doc.setFont("helvetica", "bold");
        doc.text("Mobile:", rightX, facilityMobileY);
        doc.setFont("helvetica", "normal");
        doc.text(facilityMobile, rightX + valueOffset, facilityMobileY);
      }
      y = rightY + infoLineGap * 5;
    }

    // Prescribed doctor (comment only - future use)
    // doc.setFont("helvetica", "bold");
    // doc.text("Prescribed Doctor:", rightX, rightY + 10.5);
    // doc.setFont("helvetica", "normal");
    // doc.text("--", rightX + 28, rightY + 10.5);

    // Auth Code (comment only - future use)
    // doc.setFont("helvetica", "bold");
    // doc.text("Emp No/Auth Code:", rightX, rightY + 14);
    // doc.setFont("helvetica", "normal");
    // doc.text("--", rightX + 28, rightY + 14);

    y = Math.max(
      rightY + (narrow ? 16 : 23),
      referredY + 1,
      facilityMobileY + 2,
    );

    // ✅ PDF TABLE
    const tableBody = displayItemsPdf.map((item) => [
      // item.department || "N/A",
      item.item_name || "--",

      // SAC Code (comment only - future use)
      // item.sac_code || "--",
      "--",

      item.quantity || 0,
      Number(item.item_price || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.gross || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_percent || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.tax || data?.tax || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.subtotal || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
    ]);

    doc.autoTable({
      startY: y,
      head: [
        [
          // "Department",
          "Service Description",
          "SAC Code",
          "Qty",
          "Price (Rs.)",
          "Gross",
          "Discount %",
          "Discount (Rs.)",
          "Tax",
          "Amount (Rs.)",
        ],
      ],
      body: tableBody,
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: tableFont,
        cellPadding: narrow
          ? tableCellPadding
          : { top: 2, bottom: 2, left: 1, right: 1 },
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      didParseCell: alignInvoiceLineItemsCell,
      headStyles: {
        fillColor: [199, 244, 239],
        textColor: 0,
        fontStyle: "bold",
        fontSize: tableHeadFont,
      },
      margin: { left: tableLeftMargin, right: tableRightMargin },
      pageBreak: "auto",
    });

    let currentY = doc.lastAutoTable.finalY + 3;
    if (currentY + 60 > PAGE_HEIGHT) {
      doc.addPage();
      currentY = useDrawnLetterhead ? 20 : noLetterheadTop;
    }

    if (mode === "invoice") {
      const paymentTableBody =
        settlementPdfPayments.length > 0
          ? settlementPdfPayments.map((p) => [
              formatDateTime(p?.created_at || "N/A"),
              p?.receipt_number ?? "N/A",
              displayPayMode(p?.mode_of_payment ?? data?.payment_mode),
              displayTxnRef(p?.transaction_ref_no ?? data?.ref_no),
              Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              }),
            ])
          : [
              [
                formatDateTime(payment_date),
                receipt_no,
                displayPayMode(payment_mode),
                displayTxnRef(ref_no),
                Number(amount_paid || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                }),
              ],
            ];

      doc.autoTable({
        startY: currentY,
        head: [["Payment Date", "Receipt No", "Mode", "Ref No", "Amount"]],
        body: paymentTableBody,
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          halign: "left",
          cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 2 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
        },
        margin: { left: tableLeftMargin, right: tableRightMargin },
        tableWidth: "auto",
      });
      currentY = doc.lastAutoTable.finalY + 3;
    } else {
      const receiptPaymentTableBody =
        settlementPdfPayments.length > 0
          ? settlementPdfPayments.map((p) => [
              p?.receipt_number ?? "N/A",
              displayPayMode(p?.mode_of_payment ?? data?.payment_mode),
              displayTxnRef(p?.transaction_ref_no ?? data?.ref_no),
              Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              }),
            ])
          : [
              [
                receiptNoForPdfRightHeader,
                displayPayMode(payment_mode),
                displayTxnRef(ref_no),
                Number(receiptLineAmountPdf || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                }),
              ],
            ];

      doc.autoTable({
        startY: currentY,
        head: [["Receipt No", "Mode", "Ref No", "Amount"]],
        body: receiptPaymentTableBody,
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          halign: "left",
          cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 2 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "right" },
        },
        margin: { left: tableLeftMargin, right: tableRightMargin },
        tableWidth: "wrap",
      });
      currentY = doc.lastAutoTable.finalY + 3;
    }

    // const PAGE_WIDTH = doc.internal.pageSize.width;
    const RIGHT_MARGIN = tableRightMargin;
    const RIGHT_X = PAGE_WIDTH - RIGHT_MARGIN;

    const summaryTotalsFontSize = narrow ? Math.max(5, 8 - 3) : 8;
    const summaryLineGap = narrow ? 2.8 : 5;
    const sumY = (row) =>
      narrow
        ? currentY + summaryLineGap * row
        : currentY + [0, 5, 10, 16, 21][row];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(summaryTotalsFontSize);

    if (mode === "invoice") {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(0),
        { align: "right" },
      );

      doc.text(
        `Bill Discount: Rs. ${Number(summaryPdf.invoiceLevelDiscount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (${Number(summaryPdf.invoiceDiscountPercent || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}%)`,
        RIGHT_X,
        sumY(1),
        { align: "right" },
      );

      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(2),
        { align: "right" },
      );

      doc.setTextColor(14, 63, 102);
      doc.text(
        `Amount Paid: Rs. ${Number(amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(3),
        { align: "right" },
      );

      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePdf - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(4),
        { align: "right" },
      );
    } else {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(0),
        { align: "right" },
      );
      doc.text(
        `Bill Discount: Rs. ${Number(summaryPdf.invoiceLevelDiscount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (${Number(summaryPdf.invoiceDiscountPercent || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}%)`,
        RIGHT_X,
        sumY(1),
        { align: "right" },
      );
      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(2),
        { align: "right" },
      );

      doc.setTextColor(14, 63, 102);
      doc.text(
        `Amount Paid: Rs. ${Number(receiptLineAmountPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(3),
        { align: "right" },
      );

      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePdf - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumY(4),
        { align: "right" },
      );
    }
    doc.setTextColor(0);

    const amountForWordsPdf = Math.floor(
      Number(
        (mode === "receipt" ? receiptLineAmountPdf : amount_paid) || 0,
      ),
    );
    const amountText = `${numberToWords(amountForWordsPdf)} Rupees Only`;

    const receivedText = "Received with thanks: ";
    const thanksRightEdge = narrow
      ? Math.min(amountWordsRightEdge + 6, RIGHT_X - 4)
      : RIGHT_X;
    const { thanksFs, textWidth, wrappedAmount, lineHeight } =
      getThanksInWordsLayout(
        doc,
        receivedText,
        amountText,
        thanksRightEdge,
        narrow,
      );
    const signatoryName = getSignatoryName(mode, settlementPdfPayments);
    const signatoryLineGap = narrow ? 2.8 : 4;

    const thanksBlockHeight =
      6 +
      wrappedAmount.length * lineHeight +
      (signatoryName ? signatoryLineGap : 0);

    const summaryEndY = narrow ? sumY(4) : currentY + 21;
    const gapAfterSummary = narrow ? 5 : 7;
    let thanksY = summaryEndY + gapAfterSummary;
    const footerReserve = narrow ? 6 : 22;
    if (thanksY + thanksBlockHeight + footerReserve > PAGE_HEIGHT) {
      doc.addPage();
      thanksY = useDrawnLetterhead ? (narrow ? 16 : 20) : noLetterheadTop;
    }

    doc.setFontSize(thanksFs);
    doc.setFont("helvetica", "normal");
    doc.text(receivedText, labelX, thanksY);
    doc.setFont("helvetica", "bold");
    doc.text(wrappedAmount, labelX + textWidth, thanksY);
    const lastLineY = thanksY + (wrappedAmount.length - 1) * lineHeight;

    const noteY = lastLineY + (narrow ? 5.5 : 9);
    const signatureY = narrow ? thanksY : noteY;

    doc.setFontSize(narrow ? 5.5 : 8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Note: This is a digitally generated ${mode === "receipt" ? "receipt" : "invoice"}.`,
      labelX,
      noteY,
    );

    const signatoryFontSize = narrow ? Math.max(5, 8 - 2) : 8;
    doc.setFontSize(signatoryFontSize);
    doc.setFont("helvetica", "bold");
    if (signatoryName) {
      doc.text(signatoryName, RIGHT_X, signatureY, { align: "right" });
    }
    doc.text(
      "Authorised Signatory",
      RIGHT_X,
      signatoryName ? signatureY + signatoryLineGap : signatureY,
      { align: "right" },
    );

    const downloadFileName = `${mode === "receipt" ? "Bill_Cum_Receipt" : "Invoice"}-${data?.invoice_number || "INV"}.pdf`;
    if (useTemplateLetterhead) {
      try {
        const bytes = await composeInvoiceWithLetterheadTemplate(
          doc,
          letterheadTemplate,
          letterheadTemplateType,
        );
        downloadPdfBytes(bytes, downloadFileName);
      } catch (e) {
        console.error("ViewInvoice: letterhead template compose failed", e);
        toast.error(
          "Letterhead template failed; downloading without template overlay.",
        );
        doc.save(downloadFileName);
      }
    } else {
      doc.save(downloadFileName);
    }
  };

  const handlePrint = async (withLetterhead = true) => {
    if (!data) return;

    const hasLetterheadTemplate = Boolean(
      letterheadTemplate && String(letterheadTemplate).trim(),
    );
    const useTemplateLetterhead = withLetterhead && hasLetterheadTemplate;
    const useDrawnLetterhead = withLetterhead && !hasLetterheadTemplate;

    const normalizedItems = itemState.map((it) => normalizeItem(it));
    if (!normalizedItems.length) {
      toast.error("No items found!");
      return;
    }

    const summaryPrint = getInvoiceSummaryFromItems(normalizedItems, data);
    const displayItemsPrint = mergeDisplayDiscounts(normalizedItems);
    const billAmtPrint =
      summaryPrint.billAmount > 0
        ? summaryPrint.billAmount
        : safeNumber(getFinalTotal(data));
    const discAmtPrint =
      summaryPrint.billAmount > 0 ? summaryPrint.totalDiscountAmount : 0;
    const patientPayablePrint =
      summaryPrint.billAmount > 0
        ? summaryPrint.patientPayable
        : safeNumber(getFinalTotal(data));

    const doc = new jsPDF("p", "mm", getInvoicePdfJsFormat(invoicePaperSize));
    let {
      PAGE_WIDTH,
      labelX,
      valueX,
      rightX,
      valueOffset,
      addrWrap,
      centerWrap,
      tableFont,
      tableHeadFont,
      paymentFont,
      headerTitleFont,
      sideMargin,
      narrow,
      amountWordsRightEdge,
      headerBandH,
      infoLineGap,
      contentStartY,
      titleGap,
      tableCellPadding,
      headerLogoMaxW,
      headerLogoMaxH,
      infoFontSize,
      titleFontSize,
    } = getInvoicePdfPageLayout(doc, invoicePaperSize);

    const isA5Print = invoicePaperSize === "a5";
    // Keep a little extra side breathing room for A5 direct-print so browser/printer scaling is less likely.
    let printSideMargin = isA5Print ? sideMargin + 2 : sideMargin;
    let printRightMargin = printSideMargin + 2;
    let noLetterheadTop = narrow ? 20 : 30;
    let noLetterheadBottom = narrow ? 8 : 22;
    if (!useDrawnLetterhead) {
      const nlMargins = getNoLetterheadMarginsMm(
        invoicePaperSize,
        noLetterheadMarginsByPage,
      );
      const overridden = applyWithoutLetterheadLayoutOverrides(
        {
          PAGE_WIDTH,
          labelX,
          valueX,
          rightX,
          valueOffset,
          addrWrap,
          centerWrap,
          tableFont,
          tableHeadFont,
          paymentFont,
          headerTitleFont,
          sideMargin,
          narrow,
          amountWordsRightEdge,
          headerBandH,
          infoLineGap,
          contentStartY,
          titleGap,
          tableCellPadding,
          headerLogoMaxW,
          headerLogoMaxH,
          infoFontSize,
          titleFontSize,
        },
        nlMargins,
      );
      labelX = overridden.labelX;
      valueX = overridden.valueX;
      rightX = overridden.rightX;
      addrWrap = overridden.addrWrap;
      centerWrap = overridden.centerWrap;
      sideMargin = overridden.sideMargin;
      amountWordsRightEdge = overridden.amountWordsRightEdge;
      contentStartY = overridden.contentStartY;
      printSideMargin = overridden.tableLeftMargin;
      printRightMargin = overridden.tableRightMargin;
      noLetterheadTop = overridden.noLetterheadTop;
      noLetterheadBottom = overridden.noLetterheadBottom;
    }

    const pdfPaymentsList = Array.isArray(data?.payment_details)
      ? data.payment_details
      : Array.isArray(data?.payments)
        ? data.payments
        : data?.payment
          ? [data.payment]
          : [];
    const settlementPdfPayments = sortSettlementPayments(
      pdfPaymentsList.filter(isInvoiceSettlementPayment),
    );
    const pdfPayment =
      settlementPdfPayments[0] || data?.payment || data?.payments?.[0] || {};
    const payment_date = data?.payment_date || pdfPayment?.created_at || "N/A";
    const receipt_no = data?.receipt_no || pdfPayment?.receipt_number || "N/A";
    const payment_mode =
      data?.payment_mode || pdfPayment?.mode_of_payment || "N/A";
    const ref_no = data?.ref_no || pdfPayment?.transaction_ref_no || "N/A";
    let amount_paid = 0;
    if (settlementPdfPayments.length > 0) {
      amount_paid = settlementPdfPayments.reduce(
        (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
        0,
      );
    } else {
      amount_paid = safeNumber(
        data?.amount_paid ??
          data?.partial_payment ??
          pdfPayment?.payment_amount ??
          0,
      );
    }

    const latestPdfPayment = pickLatestSettlementPayment(settlementPdfPayments);
    const receiptNoForPdfRightHeader =
      mode === "receipt"
        ? (latestPdfPayment?.receipt_number ??
          data?.receipt_no ??
          pdfPayment?.receipt_number ??
          "N/A")
        : (data?.receipt_no || pdfPayment?.receipt_number || "N/A");
    const receiptDateForPdfRightHeader =
      latestPdfPayment?.created_at ??
      data?.payment_date ??
      pdfPayment?.created_at ??
      null;
    const receiptLineAmountPdf =
      mode === "receipt"
        ? latestPdfPayment
          ? safeNumber(latestPdfPayment?.payment_amount)
          : safeNumber(
              pdfPayment?.payment_amount ??
                data?.amount_paid ??
                data?.partial_payment ??
                0,
            )
        : amount_paid;

    const printDueAmount = safeNumber(patientPayablePrint - amount_paid);
    const printStatusLabel = printDueAmount > 0.01 ? "DUE" : "PAID";

    const headerLogoDataUrl = collateralImage
      ? `data:image/png;base64,${collateralImage}`
      : null;
    const printFooterLayout = measureInvoicePdfFooter(doc, invoicePaperSize);

    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, PAGE_WIDTH, headerBandH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(
        sessionStorage.getItem("facility_display_name") ||
          data?.facility_name ||
          displayPatient?.facility_name ||
          "Super Galaxy Health",
        narrow ? 6 : 10,
        narrow ? 8 : 12,
      );

      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(
            headerLogoDataUrl,
            "PNG",
            PAGE_WIDTH - sideMargin - width,
            narrow ? 2 : 3,
            width,
            height,
          );
        }
      } catch (e) {}
    }

    function drawFooterTemplate() {
      // A5: no footer — skip entirely
      if (narrow) return;
      drawInvoicePdfFooter(doc, HlthtekFooterLogo, printFooterLayout);
    }

    // Drawn teal letterhead only. Template letterhead is composed after content.
    const bottomMarginPrint = useDrawnLetterhead
      ? narrow
        ? 8
        : Math.max(25, printFooterLayout.bandH + 8)
      : noLetterheadBottom;
    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMarginPrint,
        ...(useDrawnLetterhead ? {} : { top: noLetterheadTop }),
      },
      didDrawPage: useDrawnLetterhead
        ? function () {
            drawHeader();
            drawFooterTemplate();
          }
        : function () {},
    });

    const PAGE_HEIGHT = doc.internal.pageSize.height;
    let y = contentStartY;
    if (useDrawnLetterhead) {
      drawHeader();
    }

    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const titleText =
      mode === "receipt" ? "Bill Cum Receipt" : "Bill of Supply";
    doc.text(titleText, labelX, y);

    const statusStr = printStatusLabel;
    doc.setFontSize(narrow ? 6.5 : 9);
    if (statusStr === "PAID") {
      doc.setTextColor(6, 95, 70);
    } else {
      doc.setTextColor(153, 27, 27);
    }
    doc.text(
      `Status: ${statusStr}`,
      labelX + doc.getTextWidth(titleText) + (narrow ? 3 : 5),
      y,
    );
    doc.setTextColor(0);
    y += titleGap;

    doc.setFontSize(infoFontSize);
    doc.setFont("helvetica", "normal");
    const patientY = y;
    doc.setFont("helvetica", "bold");
    doc.text("Name:", labelX, patientY);
    doc.setFont("helvetica", "normal");
    doc.text(
      data?.patient_name || data?.patientName || displayPatient?.name || "N/A",
      valueX,
      patientY,
    );
    doc.setFont("helvetica", "bold");
    doc.text("Age/Gender:", labelX, patientY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatAgeGenderLine(displayPatient, data),
      valueX,
      patientY + infoLineGap,
    );
    doc.setFont("helvetica", "bold");
    doc.text("Contact No:", labelX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatContactNumbers(displayPatient, data),
      valueX,
      patientY + infoLineGap * 2,
    );
    doc.setFont("helvetica", "bold");
    doc.text("Address:", labelX, patientY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");

    const printPatientAddress =
      data?.address ||
      displayPatient?.address ||
      [
        displayPatient?.address_line,
        displayPatient?.locality,
        displayPatient?.city || displayPatient?.district,
        displayPatient?.state,
        displayPatient?.pincode,
      ]
        .filter(
          (v) =>
            v &&
            !["null", "undefined", "n/a"].includes(String(v).toLowerCase()),
        )
        .join(", ");

    const printPatientAddressFinal = printPatientAddress || "N/A";
    const addrLines = doc
      .splitTextToSize(printPatientAddressFinal, addrWrap)
      .slice(0, 2);
    for (let i = 0; i < addrLines.length; i++) {
      doc.text(
        addrLines[i],
        valueX,
        patientY + infoLineGap * 3 + i * infoLineGap,
      );
    }

    const printReferredY =
      patientY + infoLineGap * 3 + addrLines.length * infoLineGap;
    doc.setFont("helvetica", "bold");
    doc.text("Referred by:", labelX, printReferredY);
    doc.setFont("helvetica", "normal");
    doc.text(
      formatReferredBy(data?.referred_by || displayPatient?.referred_by),
      valueX,
      printReferredY,
    );

    const printPatId =
      displayPatient?.patient_id ||
      data?.patient_id ||
      data?.patientId ||
      "N/A";

    const rightY = patientY;
    let facilityMobileY = 0;
    doc.setFont("helvetica", "bold");
    doc.text(mode === "receipt" ? "Receipt No:" : "Bill No:", rightX, rightY);
    doc.setFont("helvetica", "normal");
    doc.text(
      mode === "receipt" ? receiptNoForPdfRightHeader : data?.invoice_number || "N/A",
      rightX + valueOffset,
      rightY,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Pat ID:", rightX, rightY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(printPatId, rightX + valueOffset, rightY + infoLineGap);

    const printCenterName =
      data?.center ||
      data?.facility_name ||
      userInfo?.facility_name ||
      sessionStorage.getItem("facility_display_name") ||
      "N/A";
    const printCenterAddress =
      data?.center_address ||
      data?.facility_address ||
      buildFacilityAddress(userInfo) ||
      "N/A";

    if (settlementPdfPayments.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(
        (mode === "receipt"
          ? formatDateTime(receiptDateForPdfRightHeader)
          : getInvoiceDateTime(data, settlementPdfPayments)) || "N/A",
        rightX + valueOffset,
        rightY + infoLineGap * 2,
      );
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const wrappedPrintCName = doc
        .splitTextToSize(printCenterName, centerWrap)
        .slice(0, 1);
      doc.text(
        wrappedPrintCName,
        rightX + valueOffset,
        rightY + infoLineGap * 3,
      );
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const wrappedCAddrStr = doc
        .splitTextToSize(printCenterAddress, centerWrap)
        .slice(0, 2);
      doc.text(wrappedCAddrStr, rightX + valueOffset, rightY + infoLineGap * 4);
      if (facilityMobile) {
        facilityMobileY = rightY + infoLineGap * (4 + wrappedCAddrStr.length);
        doc.setFont("helvetica", "bold");
        doc.text("Mobile:", rightX, facilityMobileY);
        doc.setFont("helvetica", "normal");
        doc.text(facilityMobile, rightX + valueOffset, facilityMobileY);
      }
      y = rightY + infoLineGap * 6;
    } else {
      doc.setFont("helvetica", "bold");
      doc.text("Date:", rightX, rightY + infoLineGap * 2);
      doc.setFont("helvetica", "normal");
      doc.text(
        (mode === "receipt"
          ? formatDateTime(receiptDateForPdfRightHeader)
          : getInvoiceDateTime(data, settlementPdfPayments)) || "N/A",
        rightX + valueOffset,
        rightY + infoLineGap * 2,
      );
      doc.setFont("helvetica", "bold");
      doc.text("Center:", rightX, rightY + infoLineGap * 3);
      doc.setFont("helvetica", "normal");
      const wrappedCenterNamePrint = doc
        .splitTextToSize(printCenterName, centerWrap)
        .slice(0, 1);
      doc.text(
        wrappedCenterNamePrint,
        rightX + valueOffset,
        rightY + infoLineGap * 3,
      );
      doc.setFont("helvetica", "bold");
      doc.text("Address:", rightX, rightY + infoLineGap * 4);
      doc.setFont("helvetica", "normal");
      const wrappedCAddr = doc
        .splitTextToSize(printCenterAddress, centerWrap)
        .slice(0, 2);
      doc.text(wrappedCAddr, rightX + valueOffset, rightY + infoLineGap * 4);
      if (facilityMobile) {
        facilityMobileY = rightY + infoLineGap * (4 + wrappedCAddr.length);
        doc.setFont("helvetica", "bold");
        doc.text("Mobile:", rightX, facilityMobileY);
        doc.setFont("helvetica", "normal");
        doc.text(facilityMobile, rightX + valueOffset, facilityMobileY);
      }
      y = rightY + infoLineGap * 5;
    }

    y = Math.max(
      rightY + (narrow ? 16 : 23),
      printReferredY + 1,
      facilityMobileY + 2,
    );
    const tableBody = displayItemsPrint.map((item) => [
      // item.department || "N/A",
      item.item_name || "--",
      "--",
      item.quantity || 0,
      Number(item.item_price || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.gross || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_percent || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.discount_amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.tax || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
      Number(item.subtotal || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      }),
    ]);

    doc.autoTable({
      startY: y,
      head: [
        [
          "Service Description",
          "SAC Code",
          "Qty",
          "Price (Rs.)",
          "Gross",
          "Discount %",
          "Discount (Rs.)",
          "Tax",
          "Amount (Rs.)",
        ],
      ],
      body: tableBody,
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: tableFont,
        cellPadding: narrow
          ? tableCellPadding
          : { top: 2, bottom: 2, left: 1, right: 1 },
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      didParseCell: alignInvoiceLineItemsCell,
      headStyles: { fillColor: [199, 244, 239], textColor: 0, fontStyle: "bold", fontSize: tableHeadFont },
      margin: { left: printSideMargin, right: printRightMargin },
    });

    let currentY = doc.lastAutoTable.finalY + 3;

    if (mode === "invoice") {
      const paymentTableBody =
        settlementPdfPayments.length > 0
          ? settlementPdfPayments.map((p) => [
              formatDateTime(p?.created_at || "N/A"),
              p?.receipt_number ?? "N/A",
              displayPayMode(p?.mode_of_payment ?? data?.payment_mode),
              displayTxnRef(p?.transaction_ref_no ?? data?.ref_no),
              Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              }),
            ])
          : [
              [
                formatDateTime(payment_date),
                receipt_no,
                displayPayMode(payment_mode),
                displayTxnRef(ref_no),
                Number(amount_paid || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                }),
              ],
            ];

      doc.autoTable({
        startY: currentY,
        head: [["Payment Date", "Receipt No", "Mode", "Ref No", "Amount"]],
        body: paymentTableBody,
        theme: "plain",
        tableWidth: "auto",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 2 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "left" },
          4: { halign: "right" },
        },
        margin: { left: printSideMargin, right: printRightMargin },
      });

      currentY = doc.lastAutoTable.finalY + 3;
    } else {
      const receiptPaymentTableBodyPrint =
        settlementPdfPayments.length > 0
          ? settlementPdfPayments.map((p) => [
              p?.receipt_number ?? "N/A",
              displayPayMode(p?.mode_of_payment ?? data?.payment_mode),
              displayTxnRef(p?.transaction_ref_no ?? data?.ref_no),
              Number(safeNumber(p?.payment_amount ?? 0)).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              }),
            ])
          : [
              [
                receiptNoForPdfRightHeader,
                displayPayMode(payment_mode),
                displayTxnRef(ref_no),
                Number(receiptLineAmountPdf || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                }),
              ],
            ];

      doc.autoTable({
        startY: currentY,
        head: [["Receipt No", "Mode", "Ref No", "Amount"]],
        body: receiptPaymentTableBodyPrint,
        theme: "plain",
        tableWidth: "wrap",
        styles: {
          font: "helvetica",
          fontSize: paymentFont,
          cellPadding: { top: 1.5, bottom: 1.5, left: 0, right: 2 },
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "left" },
          2: { halign: "left" },
          3: { halign: "right" },
        },
        margin: { left: printSideMargin, right: printRightMargin },
      });

      currentY = doc.lastAutoTable.finalY + 3;
    }

    const RIGHT_X = PAGE_WIDTH - printRightMargin;
    const summaryTotalsFontSizePrint = narrow ? Math.max(5, 8 - 3) : 8;
    const summaryLineGapPrint = narrow ? 2.8 : 5;
    const sumYPrint = (row) =>
      narrow
        ? currentY + summaryLineGapPrint * row
        : currentY + [0, 5, 10, 16, 21][row];
    doc.setFont("helvetica", "bold");
    doc.setFontSize(summaryTotalsFontSizePrint);
    if (mode === "invoice") {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(0),
        { align: "right" },
      );
      doc.text(
        `Bill Discount: Rs. ${Number(summaryPrint.invoiceLevelDiscount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (${Number(summaryPrint.invoiceDiscountPercent || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}%)`,
        RIGHT_X,
        sumYPrint(1),
        { align: "right" },
      );
      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(2),
        { align: "right" },
      );
      doc.setTextColor(14, 63, 102);
      doc.text(`Amount Paid: Rs. ${Number(amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(3), { align: "right" });
      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePrint - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(4),
        { align: "right" },
      );
    } else {
      doc.text(
        `Bill Amount: Rs. ${Number(billAmtPrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(0),
        { align: "right" },
      );
      doc.text(
        `Bill Discount: Rs. ${Number(summaryPrint.invoiceLevelDiscount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })} (${Number(summaryPrint.invoiceDiscountPercent || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}%)`,
        RIGHT_X,
        sumYPrint(1),
        { align: "right" },
      );
      doc.text(
        `Patient Payable: Rs. ${Number(patientPayablePrint).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(2),
        { align: "right" },
      );
      doc.setTextColor(14, 63, 102);
      doc.text(`Amount Paid: Rs. ${Number(receiptLineAmountPdf).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`, RIGHT_X, sumYPrint(3), { align: "right" });
      doc.setTextColor(0);
      doc.text(
        `Due Amount: Rs. ${Number(patientPayablePrint - amount_paid).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        RIGHT_X,
        sumYPrint(4),
        { align: "right" },
      );
    }

    doc.setTextColor(0);

    const amountForWordsPrint = Math.floor(
      Number(
        (mode === "receipt" ? receiptLineAmountPdf : amount_paid) || 0,
      ),
    );
    const amountWords = `${numberToWords(amountForWordsPrint)} Rupees Only`;

    const receivedPrint = "Received with thanks: ";
    const thanksRightEdgePrint = narrow
      ? Math.min(amountWordsRightEdge + 6, RIGHT_X - 4)
      : RIGHT_X;
    const {
      thanksFs: thanksFsPrint,
      textWidth: twPrint,
      wrappedAmount: wrappedPrintThanks,
      lineHeight: lhPrint,
    } = getThanksInWordsLayout(
      doc,
      receivedPrint,
      amountWords,
      thanksRightEdgePrint,
      narrow,
    );
    const signatoryNamePrint = getSignatoryName(mode, settlementPdfPayments);
    const signatoryLineGapPrint = narrow ? 2.8 : 4;

    const thanksHPrint =
      6 +
      wrappedPrintThanks.length * lhPrint +
      (signatoryNamePrint ? signatoryLineGapPrint : 0);

    const summaryEndYPrint = narrow ? sumYPrint(4) : currentY + 21;
    const gapAfterSummaryPrint = narrow ? 5 : 7;
    let thanksYPrint = summaryEndYPrint + gapAfterSummaryPrint;
    const footerReservePrint = narrow ? 6 : 22;
    if (thanksYPrint + thanksHPrint + footerReservePrint > PAGE_HEIGHT) {
      doc.addPage();
      thanksYPrint = useDrawnLetterhead
        ? narrow
          ? 16
          : 20
        : noLetterheadTop;
    }

    doc.setFontSize(thanksFsPrint);
    doc.setFont("helvetica", "normal");
    doc.text(receivedPrint, labelX, thanksYPrint);
    doc.setFont("helvetica", "bold");
    doc.text(wrappedPrintThanks, labelX + twPrint, thanksYPrint);
    const lastThanksY =
      thanksYPrint + (wrappedPrintThanks.length - 1) * lhPrint;

    const noteY = lastThanksY + (narrow ? 5.5 : 9);
    const signatureYPrint = narrow ? thanksYPrint : noteY;
    doc.setFontSize(narrow ? 5.5 : 8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Note: This is a digitally generated ${mode === "receipt" ? "receipt" : "invoice"}.`,
      labelX,
      noteY,
    );

    const signatoryFontSizePrint = narrow ? Math.max(5, 8 - 2) : 8;
    doc.setFontSize(signatoryFontSizePrint);
    doc.setFont("helvetica", "bold");
    if (signatoryNamePrint) {
      doc.text(signatoryNamePrint, RIGHT_X, signatureYPrint, {
        align: "right",
      });
    }
    doc.text(
      "Authorised Signatory",
      RIGHT_X,
      signatoryNamePrint
        ? signatureYPrint + signatoryLineGapPrint
        : signatureYPrint,
      { align: "right" },
    );

    if (isA5Print) {
      toast.info("For best A5 output: choose Paper Size A5 and Scale = Fit to printable area.");
    }
    if (useTemplateLetterhead) {
      try {
        const bytes = await composeInvoiceWithLetterheadTemplate(
          doc,
          letterheadTemplate,
          letterheadTemplateType,
        );
        openPdfBytesInNewTab(bytes);
      } catch (e) {
        console.error("ViewInvoice: letterhead template compose failed", e);
        toast.error(
          "Letterhead template failed; opening without template overlay.",
        );
        window.open(`${doc.output("bloburl")}#zoom=page-fit`, "_blank");
      }
    } else {
      window.open(`${doc.output("bloburl")}#zoom=page-fit`, "_blank");
    }
  };

  const handleHomeButton = () => {
    history.push("/doctor/invoice");
  };

  useEffect(() => {
    if (!externalPdfToolbar) {
      if (typeof onPdfToolbarApiReady === "function") {
        onPdfToolbarApiReady(null);
      }
      return;
    }
    if (typeof onPdfToolbarApiReady !== "function") return;
    if (!data) {
      if (typeof onPdfToolbarApiReady === "function") {
        onPdfToolbarApiReady(null);
      }
      return;
    }

    const normalizedItemsForToolbar = itemState.map((it) => normalizeItem(it));
    const summaryForToolbar = getInvoiceSummaryFromItems(
      normalizedItemsForToolbar,
      data,
    );
    const finalTotalForToolbar =
      summaryForToolbar.billAmount > 0
        ? summaryForToolbar.patientPayable
        : safeNumber(getFinalTotal(data));
    const paymentsListForToolbar = Array.isArray(data?.payment_details)
      ? data.payment_details
      : Array.isArray(data?.payments)
        ? data.payments
        : data?.payment
          ? [data.payment]
          : [];
    const settlementForToolbar = sortSettlementPayments(
      paymentsListForToolbar.filter(isInvoiceSettlementPayment),
    );
    let amountPaidForToolbar = 0;
    if (settlementForToolbar.length > 0) {
      amountPaidForToolbar = settlementForToolbar.reduce(
        (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
        0,
      );
    } else {
      const paymentFallback =
        data?.payment || data?.payments?.[0] || {};
      amountPaidForToolbar = safeNumber(
        data?.amount_paid ??
          data?.partial_payment ??
          paymentFallback?.payment_amount ??
          0,
      );
    }
    const balanceForToolbar = safeNumber(
      finalTotalForToolbar - amountPaidForToolbar,
    );
    const isActuallyPaidToolbar = balanceForToolbar <= 0.01;
    const hasPaymentToolbar = amountPaidForToolbar > 0.01;
    const canDownloadForToolbar =
      mode === "invoice" ? isActuallyPaidToolbar : hasPaymentToolbar;

    onPdfToolbarApiReady({
      invoicePaperSize,
      setInvoicePaperSize,
      invoicePrintType,
      setInvoicePrintType,
      canDownloadPDF: canDownloadForToolbar,
      openDownloadMenu: (anchorEl) => {
        if (anchorEl) setDownloadMenuAnchorEl(anchorEl);
      },
      openPrintMenu: (anchorEl) => {
        if (anchorEl) setPrintMenuAnchorEl(anchorEl);
      },
      closeMenus: () => {
        setDownloadMenuAnchorEl(null);
        setPrintMenuAnchorEl(null);
      },
    });
    return () => {
      if (typeof onPdfToolbarApiReady === "function") {
        onPdfToolbarApiReady(null);
      }
    };
  }, [
    externalPdfToolbar,
    onPdfToolbarApiReady,
    invoicePaperSize,
    invoicePrintType,
    data,
    mode,
    itemState,
  ]);

  if (!data) return null;

  const normalizedItemsForUI = itemState.map((it) => normalizeItem(it));

  const summaryUi = getInvoiceSummaryFromItems(normalizedItemsForUI, data);
  const displayItemsForUI = mergeDisplayDiscounts(normalizedItemsForUI);
  const billAmountUi =
    summaryUi.billAmount > 0
      ? summaryUi.billAmount
      : safeNumber(getFinalTotal(data));
  const FINAL_TOTAL_UI =
    summaryUi.billAmount > 0
      ? summaryUi.patientPayable
      : safeNumber(getFinalTotal(data));

  const invoiceTax = safeNumber(data?.tax ?? 0);

  // Support payment_details (get_all_invoices), payments[], or single payment
  const paymentsList = Array.isArray(data?.payment_details)
    ? data.payment_details
    : Array.isArray(data?.payments)
      ? data.payments
      : data?.payment
        ? [data.payment]
        : [];
  const settlementPayments = sortSettlementPayments(
    paymentsList.filter(isInvoiceSettlementPayment),
  );
  const payment =
    settlementPayments[0] || data?.payment || data?.payments?.[0] || {};
  const payment_date = data?.payment_date || payment?.created_at || "N/A";
  const receipt_no = data?.receipt_no || payment?.receipt_number || "N/A";
  const payment_mode =
    data?.payment_mode || payment?.mode_of_payment || "N/A";
  const ref_no = data?.ref_no || payment?.transaction_ref_no || "N/A";
  let amount_paid = 0;
  if (settlementPayments.length > 0) {
    amount_paid = settlementPayments.reduce(
      (sum, p) => sum + safeNumber(p?.payment_amount ?? 0),
      0,
    );
  } else {
    amount_paid = safeNumber(
      data?.amount_paid ??
        data?.partial_payment ??
        payment?.payment_amount ??
        0,
    );
  }

  const latestUiPayment = pickLatestSettlementPayment(settlementPayments);
  const receiptNoForHeader =
    latestUiPayment?.receipt_number ??
    data?.receipt_no ??
    payment?.receipt_number ??
    "N/A";
  const receiptDateForHeader =
    latestUiPayment?.created_at ?? data?.payment_date ?? payment?.created_at ?? null;
  const receiptLineAmountUi =
    mode === "receipt"
      ? latestUiPayment
        ? safeNumber(latestUiPayment?.payment_amount)
        : safeNumber(
            payment?.payment_amount ??
              data?.amount_paid ??
              data?.partial_payment ??
              0,
          )
      : amount_paid;

  const pStatus = (data?.payment_status || "UNPAID").toUpperCase();
  const balance = safeNumber(FINAL_TOTAL_UI - amount_paid);
  const displayStatusBadge = balance > 0.01 ? "DUE" : "PAID";
  const isActuallyPaid = balance <= 0.01;
  const hasPayment = amount_paid > 0.01;

  // Invoice mode: Only download if fully paid (balance is 0)
  // Receipt mode: Download if at least something is paid
  const canDownloadPDF = mode === "invoice" ? isActuallyPaid : hasPayment;

  return (
    <>
      {permissionLoading ? null : (
        <Card style={{ marginBottom: "16px" }}>
          {/* Main Card */}
          <Card
            style={{
              width: "95%",
              height: "100%",
              padding: "16px",
              marginLeft: "40px",
              marginTop: "1%",
            }}
          >
            <div id="invoice-template">
              <Grid
                container
                style={{
                  padding: "50px",
                  fontSize: "18px",
                  lineHeight: "25px",
                  paddingBottom: "20px",
                }}
              >
                {/* LEFT SECTION */}
                <Grid item xs={7}>
                  <div
                    style={{
                      display: "grid",
                      rowGap: "2px",
                      lineHeight: 1.35,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      <div style={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {mode === "receipt"
                          ? "Bill Cum Receipt"
                          : "Bill of Supply"}
                      </div>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          color:
                            displayStatusBadge === "PAID"
                              ? "#065f46"
                              : "#991b1b",
                          backgroundColor:
                            displayStatusBadge === "PAID"
                              ? "#d1fae5"
                              : "#fee2e2",
                        }}
                      >
                        {displayStatusBadge}
                      </Box>
                    </div>
                    <div>
                      <b>Name:</b>{" "}
                      {data?.patient_name ||
                        data?.patientName ||
                        displayPatient?.name ||
                        "N/A"}
                    </div>
                    <div>
                      <b>Age/Gender:</b>{" "}
                      {formatAgeGenderLine(displayPatient, data)}
                    </div>
                    <div>
                      <b>Contact No:</b>{" "}
                      {formatContactNumbers(displayPatient, data)}
                    </div>

                    {/* UHID (comment only - future use) */}
                    {/*
                  <div>
                    <b>UHID:</b> {data?.uhid || patient?.uhid || "N/A"}
                  </div>
                  */}

                    <div
                      style={{
                        maxWidth: "420px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      <b>Address:</b>{" "}
                      {data?.address ||
                        displayPatient?.address ||
                        [
                          displayPatient?.address_line,
                          displayPatient?.locality,
                          displayPatient?.city || displayPatient?.district,
                          displayPatient?.state,
                          displayPatient?.pincode,
                        ]
                          .filter(
                            (v) =>
                              v &&
                              !["null", "undefined", "n/a"].includes(
                                v.toString().toLowerCase(),
                              ),
                          )
                          .join(", ") ||
                        "N/A"}
                    </div>
                    <div>
                      <b>Referred by:</b>{" "}
                      {formatReferredBy(
                        data?.referred_by || displayPatient?.referred_by,
                      )}
                    </div>
                  </div>
                </Grid>

                {/* RIGHT SECTION */}
                <Grid
                  item
                  xs={5}
                  style={{ textAlign: "left", paddingLeft: "230px" }}
                >
                  <div style={{ display: "grid", rowGap: "4px" }}>
                    <div>
                      <b>{mode === "receipt" ? "Receipt No:" : "Bill No:"}</b>{" "}
                      {mode === "receipt"
                        ? receiptNoForHeader
                        : data?.invoice_number || "N/A"}
                    </div>

                    <div>
                      <b>Pat ID:</b>{" "}
                      {displayPatient?.patient_id ||
                        data?.patient_id ||
                        data?.patientId ||
                        "N/A"}
                    </div>

                    <div>
                      <b>Date:</b>{" "}
                      {mode === "receipt"
                        ? formatDateTime(receiptDateForHeader)
                        : getInvoiceDateTime(data, settlementPayments)}
                    </div>

                    {/* Prescribed Doctor (comment only - future use) */}
                    {/*
                  <div>
                    <b>Prescribed Doctor:</b> --
                  </div>
                  */}

                    {/* Auth Code (comment only - future use) */}
                    {/*
                  <div>
                    <b>Emp No/Auth Code:</b> --
                  </div>
                  */}

                    <div>
                      <b>Center:</b>{" "}
                      {data?.center ||
                        data?.facility_name ||
                        userInfo?.facility_name ||
                        sessionStorage.getItem("facility_display_name") ||
                        "N/A"}
                    </div>

                    <div style={{ maxWidth: "420px", wordBreak: "break-word" }}>
                      <b>Center Address:</b>{" "}
                      {data?.center_address ||
                        data?.facility_address ||
                        buildFacilityAddress(userInfo) ||
                        "N/A"}
                    </div>

                    {facilityMobile && (
                      <div>
                        <b>Mobile:</b> {facilityMobile}
                      </div>
                    )}
                  </div>
                </Grid>
              </Grid>

              {/* Invoice Card */}
              <Card style={{ height: "auto" }}>
                {/* Invoice Header */}
                <Grid
                  container
                  style={{
                    backgroundColor: "#C7F4EF",
                    padding: "16px 20px",
                    alignItems: "center",
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight="bold">
                      {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} textAlign="right">
                    {collateralImage && (
                      <img
                        src={`data:image/png;base64,${collateralImage}`}
                        height="70"
                        style={{ maxWidth: "220px", objectFit: "contain" }}
                        alt="logo"
                      />
                    )}
                  </Grid>
                </Grid>

                {/* Table — same font as patient header (18px) so payment rows can match */}
                <Grid
                  item
                  xs={12}
                  style={{
                    padding: "20px",
                    display: "flex",
                    justifyContent: "left",
                    width: "100vw",
                    fontSize: "18px",
                    lineHeight: "25px",
                  }}
                >
                  <div style={{ width: "90%" }}>
                    {/* TABLE HEADER */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2.2fr 0.9fr 0.7fr 1fr 1fr 1fr 1fr 0.8fr 1fr",
                        columnGap: 1,
                        fontWeight: "bold",
                        padding: "10px 0",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {/* Department */}
                      <Box>Service Description</Box>
                      <Box sx={{ textAlign: "center" }}>SAC Code</Box>
                      <Box sx={{ textAlign: "center" }}>Qty</Box>
                      <Box sx={{ textAlign: "right" }}>Price (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Gross Value</Box>
                      <Box sx={{ textAlign: "right" }}>Discount %</Box>
                      <Box sx={{ textAlign: "right" }}>Discount (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Tax</Box>
                      <Box sx={{ textAlign: "right" }}>Amount (₹)</Box>
                    </Box>

                    {/* TABLE ROWS */}
                    {displayItemsForUI.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2.2fr 0.9fr 0.7fr 1fr 1fr 1fr 1fr 0.8fr 1fr",
                          columnGap: 1,
                          borderBottom: "1px solid #eee",
                          padding: "10px 0",
                        }}
                      >
                        {/* Department */}
                        <Box>
                          {item.item_name || "--"}
                          {item.ordered_by?.doctor_name && (
                            <Box sx={{ fontSize: "13px", color: "#6c757d" }}>
                              Ordered by: {item.ordered_by.doctor_name}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ textAlign: "center" }}>--</Box>
                        <Box sx={{ textAlign: "center" }}>{item.quantity}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.item_price)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.gross)}</Box>
                        <Box sx={{ textAlign: "right" }}>{formatMoney(item.discount_percent)}%</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.discount_amount)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.tax || invoiceTax || 0)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.subtotal)}</Box>
                      </Box>
                    ))}
                  </div>
                </Grid>

                {/* Total + Payment */}
                <Grid
                  container
                  style={{
                    padding: "20px",
                    marginTop: "20px",
                    alignItems: "flex-start",
                    fontSize: "18px",
                    lineHeight: "25px",
                  }}
                >
                  {/* LEFT */}
                  <Grid item xs={7}>
                    {mode === "invoice" ? (
                      <>
                        <Grid
                          container
                          style={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "6px",
                          }}
                        >
                          <Grid item xs={3}>
                            Payment Settlement
                          </Grid>
                          <Grid item xs={2}>
                            Receipt No
                          </Grid>
                          <Grid item xs={2}>
                            Mode
                          </Grid>
                          <Grid item xs={3}>
                            Ref.No
                          </Grid>
                          <Grid item xs={2} style={{ display: "flex", justifyContent: "flex-end" }}>
                            Amount
                          </Grid>
                        </Grid>

                        {settlementPayments.length > 0 ? (
                          settlementPayments.map((p, idx) => (
                            <Grid
                              key={p?.payment_id ?? p?.receipt_number ?? idx}
                              container
                              style={{ paddingTop: "6px" }}
                            >
                              <Grid item xs={3}>
                                {formatDateTime(
                                  p?.created_at || data?.payment_date || "N/A",
                                )}
                              </Grid>
                              <Grid item xs={2}>
                                {p?.receipt_number ?? data?.receipt_no ?? "N/A"}
                              </Grid>
                              <Grid item xs={2}>
                                {displayPayMode(
                                  p?.mode_of_payment ?? data?.payment_mode,
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={3}
                                style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                              >
                                {displayTxnRef(
                                  p?.transaction_ref_no ?? data?.ref_no,
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={2}
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "flex-start",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                ₹{formatMoney(p?.payment_amount ?? 0)}
                              </Grid>
                            </Grid>
                          ))
                        ) : (
                          <Grid container style={{ paddingTop: "6px" }}>
                            <Grid item xs={3}>
                              {formatDateTime(payment_date)}
                            </Grid>
                            <Grid item xs={2}>
                              {receipt_no}
                            </Grid>
                            <Grid item xs={2}>
                              {displayPayMode(payment_mode)}
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                            >
                              {displayTxnRef(ref_no)}
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "flex-start",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              ₹{formatMoney(amount_paid)}
                            </Grid>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid
                          container
                          style={{
                            fontWeight: "bold",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "6px",
                          }}
                        >
                          <Grid item xs={3}>
                            Receipt No
                          </Grid>
                          <Grid item xs={3}>
                            Mode
                          </Grid>
                          <Grid item xs={3}>
                            Ref.No
                          </Grid>
                          <Grid item xs={3} style={{ display: "flex", justifyContent: "flex-end" }}>
                            Amount
                          </Grid>
                        </Grid>

                        {settlementPayments.length > 0 ? (
                          settlementPayments.map((p, idx) => (
                            <Grid
                              key={p?.payment_id ?? p?.receipt_number ?? idx}
                              container
                              style={{ paddingTop: "6px" }}
                            >
                              <Grid item xs={3}>
                                {p?.receipt_number ?? data?.receipt_no ?? "N/A"}
                              </Grid>
                              <Grid item xs={3}>
                                {displayPayMode(
                                  p?.mode_of_payment ?? data?.payment_mode,
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={3}
                                style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                              >
                                {displayTxnRef(
                                  p?.transaction_ref_no ?? data?.ref_no,
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={3}
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  alignItems: "flex-start",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                ₹{formatMoney(p?.payment_amount ?? 0)}
                              </Grid>
                            </Grid>
                          ))
                        ) : (
                          <Grid container style={{ paddingTop: "6px" }}>
                            <Grid item xs={3}>
                              {receiptNoForHeader}
                            </Grid>
                            <Grid item xs={3}>
                              {displayPayMode(payment_mode)}
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              style={{ wordBreak: "break-all", overflowWrap: "anywhere" }}
                            >
                              {displayTxnRef(ref_no)}
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "flex-start",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              ₹{formatMoney(receiptLineAmountUi)}
                            </Grid>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>

                  {/* RIGHT */}
                  <Grid
                    item
                    xs={5}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <div style={{ textAlign: "right" }}>
                      {mode === "invoice" ? (
                        <>
                          <Typography>
                            Bill Amount: ₹{formatMoney(billAmountUi)}
                          </Typography>
                          <Typography>
                            Bill Discount: ₹
                            {formatMoney(summaryUi.invoiceLevelDiscount)} (
                            {formatMoney(summaryUi.invoiceDiscountPercent)}%)
                          </Typography>
                          <Typography>
                            Patient Payable: ₹{formatMoney(FINAL_TOTAL_UI)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px", color: "#0e3f66" }}
                          >
                            Amount Paid: ₹{formatMoney(amount_paid)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px" }}
                          >
                            Due Amount: ₹
                            {formatMoney(FINAL_TOTAL_UI - amount_paid)}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography>
                            Bill Amount: ₹{formatMoney(billAmountUi)}
                          </Typography>
                          <Typography>
                            Bill Discount: ₹
                            {formatMoney(summaryUi.invoiceLevelDiscount)} (
                            {formatMoney(summaryUi.invoiceDiscountPercent)}%)
                          </Typography>
                          <Typography>
                            Patient Payable: ₹{formatMoney(FINAL_TOTAL_UI)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px", color: "#0e3f66" }}
                          >
                            Amount Paid: ₹{formatMoney(receiptLineAmountUi)}
                          </Typography>
                          <Typography
                            fontWeight="bold"
                            style={{ marginTop: "8px" }}
                          >
                            Due Amount: ₹
                            {formatMoney(FINAL_TOTAL_UI - amount_paid)}
                          </Typography>
                        </>
                      )}

                      {/* Moved Amount Paid inside mode-specific blocks */}
                    </div>
                  </Grid>
                </Grid>

                {/* Footer Terms */}
                <Grid
                  container
                  style={{ padding: "20px", alignItems: "flex-end" }}
                >
                  <Grid item xs={8}>
                    <Typography variant="subtitle1">
                      Received with thanks:{" "}
                      <b>
                        {numberToWords(
                          Math.floor(
                            Number(
                              (mode === "receipt"
                                ? receiptLineAmountUi
                                : amount_paid) || 0,
                            ),
                          ),
                        )}{" "}
                        Rupee Only
                      </b>
                    </Typography>
                  </Grid>

                  <Grid item xs={4} style={{ textAlign: "right" }}>
                    <Box>
                      {getSignatoryName(mode, settlementPayments) && (
                        <Typography variant="subtitle1" component="div" fontWeight="bold">
                          {getSignatoryName(mode, settlementPayments)}
                        </Typography>
                      )}
                      <Typography variant="subtitle1" component="div" fontWeight="bold">
                        Authorised Signatory
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                {getInvoiceNotesText(data) && (
                  <Grid container style={{ padding: "0 20px" }}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <b>Notes:</b> {getInvoiceNotesText(data)}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                <Grid
                  container
                  style={{ padding: "10px 20px", alignItems: "center" }}
                >
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Note: This is a digitally generated{" "}
                      {mode === "receipt" ? "receipt" : "invoice"}.
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </div>

            {!externalPdfToolbar && (
              <>
            <Grid
              container
              justifyContent="center"
              style={{ marginTop: "24px" }}
            >
              <Grid item xs={12} sm={8} md={4}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="invoice-paper-size-label">
                    Paper size (PDF and print)
                  </InputLabel>
                  <Select
                    labelId="invoice-paper-size-label"
                    id="invoice-paper-size"
                    label="Paper size (PDF and print)"
                    value={invoicePaperSize}
                    onChange={(e) =>
                      setInvoicePaperSize(
                        normalizeInvoicePageSizeKey(e.target.value),
                      )
                    }
                  >
                    <MenuItem value="a4">A4 (210 × 297 mm)</MenuItem>
                    <MenuItem value="a5">A5 (148 × 210 mm)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  size="small"
                  variant="outlined"
                  style={{ marginTop: "16px" }}
                >
                  <InputLabel id="invoice-print-type-label">
                    Print type
                  </InputLabel>
                  <Select
                    labelId="invoice-print-type-label"
                    id="invoice-print-type"
                    label="Print type"
                    value={invoicePrintType}
                    onChange={(e) =>
                      setInvoicePrintType(
                        normalizeInvoicePrintType(e.target.value),
                      )
                    }
                  >
                    <MenuItem value="LETTERHEAD">
                      {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}{" "}
                      with Letterhead
                    </MenuItem>
                    <MenuItem value="WITHOUT_LETTERHEAD">
                      {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}{" "}
                      without Letterhead
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* A5 printing tip (hidden)
            {invoicePaperSize === "a5" && (
              <Grid container justifyContent="center" style={{ marginTop: "16px" }}>
                <Grid item xs={12} sm={10} md={8}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f0fdfa",
                      border: "1px solid #5eead4",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle2" color="#0f766e" fontWeight="bold">
                      A5 Printing Tip:
                    </Typography>
                    <Typography variant="body2" color="#134e4a">
                      In the print dialog, please set <b>Paper Size to A5</b> and <b>Scale to 100%</b> (no scaling/fit to page) for perfect results.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            */}

            {/* Buttons */}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={4}
              style={{ marginTop: "16px" }}
            >
              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={
                    canDownloadPDF
                      ? () =>
                          handleDownload(invoicePrintType === "LETTERHEAD")
                      : undefined
                  }
                  disabled={!canDownloadPDF}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: canDownloadPDF ? "#21cdc0" : "#ccc",
                    color: canDownloadPDF ? "#ffffffff" : "#666",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: canDownloadPDF ? "pointer" : "not-allowed",
                  }}
                >
                  DOWNLOAD PDF
                </button>
              </Grid>

              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={
                    canDownloadPDF
                      ? () => handlePrint(invoicePrintType === "LETTERHEAD")
                      : undefined
                  }
                  disabled={!canDownloadPDF}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: canDownloadPDF ? "#21cdc0" : "#ccc",
                    color: canDownloadPDF ? "#ffffffff" : "#666",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: canDownloadPDF ? "pointer" : "not-allowed",
                  }}
                >
                  PRINT
                </button>
              </Grid>

              <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                <button
                  type="button"
                  onClick={handleHomeButton}
                  style={{
                    width: "100%",
                    padding: "18px 0",
                    backgroundColor: "#21cdc0",
                    color: "#fff",
                    borderRadius: "10px",
                    fontSize: "22px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  GO TO HOME
                </button>
              </Grid>
            </Grid>
              </>
            )}

            {externalPdfToolbar && (
              <>
                <Menu
                  anchorEl={downloadMenuAnchorEl}
                  open={Boolean(downloadMenuAnchorEl)}
                  onClose={() => setDownloadMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    style: { minWidth: 220 },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleDownload(true);
                      setDownloadMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} with
                    Letterhead
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleDownload(false);
                      setDownloadMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}{" "}
                    without Letterhead
                  </MenuItem>
                </Menu>
                <Menu
                  anchorEl={printMenuAnchorEl}
                  open={Boolean(printMenuAnchorEl)}
                  onClose={() => setPrintMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    style: { minWidth: 220 },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handlePrint(true);
                      setPrintMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"} with
                    Letterhead
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handlePrint(false);
                      setPrintMenuAnchorEl(null);
                    }}
                  >
                    {mode === "receipt" ? "Bill Cum Receipt" : "Invoice"}{" "}
                    without Letterhead
                  </MenuItem>
                </Menu>
              </>
            )}
          </Card>
        </Card>
      )}
    </>
  );
};

export default ViewInvoice;
